import React, { useState, useRef, useEffect, useCallback } from 'react';
import { B } from '../constants';
import { PLANNER_MODELS } from '../data/plannerConfig';
import { ai, destinations as destinationsAPI, trips as tripsAPI } from '../api';
import useDebouncedDestSearch from '../hooks/useDebouncedDestSearch';
import MapView from '../components/MapView';
import DestinationCard from '../components/DestinationCard';
import FallbackImg, { FullPhoto } from '../components/FallbackImg';
import ApproximateLocationNote from '../components/ApproximateLocationNote';
import PlaceSheet, { categoryLabel } from '../components/PlaceSheet';
import ShareModal from '../components/ShareModal';
import { saveSelectedDestination, loadSelectedDestination } from '../lib/plannerDestination';
import Modal from '../components/ui/Modal';
import { monthRanges } from '../lib/months';
import { geocodeWithCache } from '../lib/geocode';
import { directionsUrl } from '../lib/directions';
import Button from '../components/ui/Button';
import TripCraftingLoader from '../components/TripCraftingLoader';
import FeedbackButton from '../components/FeedbackButton';
import { trackActivity } from '../lib/activityEvents';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, info: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { this.setState({ info }); console.log(`[EB:${this.props.name}]`, error, info.componentStack); }
  render() {
    if (this.state.hasError) {
      return <div style={{ background: '#ffd', border: '2px solid red', borderRadius: 8, padding: 16, margin: 8 }}>
        <h3 style={{ color: 'red', margin: 0 }}>⚠ Error in {this.props.name}</h3>
        <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{this.state.error?.toString()}</pre>
      </div>;
    }
    return this.props.children;
  }
}

const PSTEPS = [
  { key: 'dest', label: 'Destination', title: 'Where would you like to go?', type: 'dest' },
  { key: 'dates', label: 'Dates', title: 'When are you going?', type: 'date' },
  {
    // The "Up to ~N stops" copy below must match PACE_PRESETS.maxAttractions in
    // backend/src/lib/itineraryPlanner.js — it promises what the planner will fit in a day.
    key: 'pace', label: 'Pace', title: 'How many places per day?',
    opts: [
      { v: 'relaxed', h: 'Relaxed', p: 'Up to ~4 stops · relaxed days', em: '🌿', sum: 'Relaxed' },
      { v: 'balanced', h: 'Balanced', p: 'Up to ~5 stops · a full day', em: '⚖️', sum: 'Balanced' },
      { v: 'packed', h: 'Packed', p: 'Up to ~8 stops · dawn to dusk', em: '🚀', sum: 'Packed' },
    ],
  },
  {
    key: 'budget', label: 'Style & budget', title: "What's your style & budget?",
    opts: [
      { v: 0.7, h: 'Smart & thrifty', p: 'Hostels, local eats', em: '🎒', sum: 'Budget' },
      { v: 1, h: 'Comfort', p: '3–4★ hotels, mix', em: '🏨', sum: 'Comfort' },
      { v: 1.8, h: 'Luxury', p: '5★ stays, fine dining', em: '💎', sum: 'Luxury' },
    ],
  },
  {
    key: 'who', label: 'Travelers', title: "Who's coming along?",
    opts: [
      { v: 1, h: 'Solo', p: 'Just me', em: '🧍', sum: 'Solo' },
      { v: 2, h: 'Couple', p: 'Two travelers', em: '💑', sum: 'Couple' },
      { v: 4, h: 'Family', p: '3–5 people', em: '👨‍👩‍👧', sum: 'Family' },
      { v: 6, h: 'Friends', p: 'Group trip', em: '👯', sum: 'Friends' },
    ],
  },
  {
    // Optional, multi-select. `v` is the phrase sent as `interests`; the backend maps it
    // to place categories in itineraryRag's INTEREST_CATEGORY_MAP, so keep them in sync.
    key: 'interests', label: 'Interests', title: 'What do you enjoy? (optional)', multi: true,
    opts: [
      { v: 'history', h: 'History & heritage', p: 'Landmarks, old towns, churches', em: '🏛️', sum: 'History' },
      { v: 'art museum', h: 'Museums & art', p: 'Museums and galleries', em: '🖼️', sum: 'Museums' },
      { v: 'nature park', h: 'Parks & nature', p: 'Parks and gardens', em: '🌳', sum: 'Nature' },
      { v: 'scenic viewpoint', h: 'Views & photos', p: 'Viewpoints and lookouts', em: '📸', sum: 'Views' },
      { v: 'beach', h: 'Beaches', p: 'Time by the water', em: '🏖️', sum: 'Beaches' },
      { v: 'adventure', h: 'Adventure', p: 'Trails, hikes, activities', em: '🧗', sum: 'Adventure' },
      { v: 'wildlife', h: 'Wildlife', p: 'Zoos, aquariums, reserves', em: '🦁', sum: 'Wildlife' },
      { v: 'wellness', h: 'Wellness', p: 'Spas and hot springs', em: '🧖', sum: 'Wellness' },
    ],
  },
  {
    key: 'transport', label: 'Transport', title: "How will you get around?",
    opts: [
      { v: 'foot', h: 'Walking', p: 'Walk everywhere — no tickets needed', em: '🚶', sum: 'Walking' },
      { v: 'transit', h: 'Public Transit', p: 'Metro, buses & trains only (no taxi or rideshare)', em: '🚇', sum: 'Transit' },
      { v: 'rideshare', h: 'Taxi & Rideshare', p: 'Uber, Bolt, Grab or local taxis, door to door', em: '🚕', sum: 'Rideshare' },
      { v: 'car', h: 'Own / Rental Car', p: 'You drive yourself and park', em: '🚗', sum: 'Car' },
    ],
  },
];

// Travel modes the backend planner reports on each leg (`act.travel.mode`).
const MODE_META = {
  walk: { em: '🚶', label: 'Walking' },
  transit: { em: '🚇', label: 'Public transit' },
  rideshare: { em: '🚕', label: 'Taxi / rideshare' },
  drive: { em: '🚗', label: 'Driving' },
};
const modeMeta = mode => MODE_META[mode] || MODE_META.drive;
const FLEX_DAY_OPTIONS = Array.from({ length: 14 }, (_, k) => k + 1);

// Emoji for the day-trip header note, mirroring the transport step's icons. The
// backend echoes the plan's transport mode back as `dayTrip.transport`; unknown
// modes fall back to rail — the note's previous hardcoded emoji.
const DAY_TRIP_TRANSPORT_EMOJI = { car: '🚗', rideshare: '🚉', foot: '🚶', transit: '🚄', rail: '🚄' };
const dayTripTransportEmoji = transport => DAY_TRIP_TRANSPORT_EMOJI[transport] || '🚄';

// Static, destination-agnostic guidance per chosen transport mode. Kept generic on
// purpose — we don't have verified per-city pass pricing.
const TRANSPORT_NOTES = {
  foot: 'Your days are planned to be walkable end to end, so no tickets or passes are needed.',
  transit: 'Most cities sell a 24/48/72-hour transit pass or a stored-value card (Oyster, Suica, Navigo, EZ-Link…). It usually beats single tickets from about three rides a day — buy it at any metro station or in the local operator app.',
  rideshare: 'Rideshare apps (Uber, Bolt, Grab or the local equivalent) quote fares upfront, which is usually safer than a metered street taxi. Airports often have a fixed-price pickup zone.',
  car: 'Check for congestion or low-emission zone charges in the centre and pre-book a garage near the old town — street parking is normally time-limited.',
};

// Aggregate the per-leg travel data the planner attaches to each activity into a
// single "getting around" view: totals plus a breakdown by mode.
function travelSummary(itin) {
  const legs = [];
  for (const day of itin?.days || []) {
    for (const act of day.activities || []) {
      if (act?.travel && typeof act.travel.minutes === 'number') legs.push(act.travel);
    }
  }
  if (!legs.length) return null;
  const byMode = new Map();
  for (const leg of legs) {
    const entry = byMode.get(leg.mode) || { mode: leg.mode, legs: 0, minutes: 0, km: 0 };
    entry.legs += 1;
    entry.minutes += leg.minutes || 0;
    entry.km += leg.km || 0;
    byMode.set(leg.mode, entry);
  }
  const totalMinutes = legs.reduce((n, l) => n + (l.minutes || 0), 0);
  const dayCount = itin.days.length || 1;
  return {
    modes: [...byMode.values()]
      .map(m => ({ ...m, km: Math.round(m.km * 10) / 10 }))
      .sort((a, b) => b.legs - a.legs),
    totalMinutes,
    perDayMinutes: Math.round(totalMinutes / dayCount),
  };
}

// Trim an itinerary down to the fields /api/ai/refine needs. The full object
// carries photo URLs, alternatives and prose that can push the request past the
// backend's 1 MB JSON limit — which surfaces to the user as a refine failure.
function slimForRefine(itin) {
  return {
    title: itin.title,
    summary: itin.summary,
    totalBudgetEstimate: itin.totalBudgetEstimate,
    bestTimeToVisit: itin.bestTimeToVisit,
    days: (itin.days || []).map(d => ({
      day: d.day, title: d.title, theme: d.theme, icon: d.icon,
      activities: (d.activities || []).map(a => ({
        ref: a.ref,
        activity: a.activity || a.name,
        time: a.time,
        placeId: a.placeId,
        kbId: a.kbId,
        lat: a.lat,
        lng: a.lng,
        description: a.description,
        cost: a.cost,
        tip: a.tip,
      })),
    })),
  };
}

// Refine returns prose + ordering only; carry the local-only fields (photos and
// the planner's travel legs) across by `ref` so the map and the transport summary
// survive a refinement.
function carryOverLocalFields(next, previous) {
  const byRef = new Map();
  for (const day of previous?.days || []) {
    for (const act of day.activities || []) if (act?.ref != null) byRef.set(act.ref, act);
  }
  if (!byRef.size) return next;
  return {
    ...next,
    days: (next.days || []).map(d => ({
      ...d,
      activities: (d.activities || []).map(a => {
        const prev = a?.ref != null ? byRef.get(a.ref) : null;
        if (!prev) return a;
        return { ...a, travel: a.travel || prev.travel, photoUrl: a.photoUrl || prev.photoUrl, kbId: a.kbId ?? prev.kbId };
      }),
    })),
  };
}

// Humanize a minute count for travel notes ("135" → "2h15").
function humanizeMinutes(min) {
  const m = Math.max(0, Number(min) || 0);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h${String(rem).padStart(2, '0')}` : `${h}h`;
}

// Re-shape the /daytrip-swap response (`days`) into the screen's itinerary day
// shape so the existing timeline / travel-chip / summary render paths work
// unchanged. The vacated day carries `dayTrip` metadata for the DAY TRIP badge +
// travel note; re-packed former stops carry `movedFrom` for the "moved in" hint.
// The response echoes only {ref, name, lat, lng, durationMin} per stop, so the
// local-only fields (time slot, description, cost, tip, photo, placeId, "near"
// note, travel leg) are carried over from the pre-swap itinerary by ref, with a
// name+position fallback for stops the backend re-names — absorbed AND surviving
// stops keep showing exactly as they did before the swap. The day-trip day's own
// stops are brand new and keep the response's own travel (the outbound leg), so
// they are exempt from the carry, as are synthetic placeholders.
function swapResultToItin(result, base) {
  const byRef = new Map();
  const byName = new Map();
  for (const day of base?.days || []) {
    for (const act of day.activities || []) {
      if (act?.ref != null) byRef.set(act.ref, act);
      const key = act.activity || act.name || '';
      if (!key) continue;
      const list = byName.get(key) || [];
      list.push(act);
      byName.set(key, list);
    }
  }
  const nameCursor = new Map();
  const findLocal = (s) => {
    if (s?.ref != null && byRef.has(s.ref)) return byRef.get(s.ref);
    const key = s?.name || '';
    const list = (key && byName.get(key)) || [];
    const i = nameCursor.get(key) || 0;
    if (i >= list.length) return null;
    nameCursor.set(key, i + 1);
    return list[i];
  };
  return {
    ...base,
    days: (result && result.days ? result.days : []).map((d) => ({
      day: d.dayNumber,
      title: d.title || '',
      icon: d.icon || '📍',
      theme: d.theme || '',
      dayTrip: d.dayTrip || null,
      activities: (d.stops || []).map((s) => {
        const act = {
          ref: s.ref,
          activity: s.name,
          time: s.time,
          lat: s.lat,
          lng: s.lng,
          description: s.description,
          travel: s.travel,
          // The day-trip day's stops are brand new and only the response carries
          // their identity fields; read the photo (the gb:place target sends
          // `photoUrl` too) or the day renders imageless.
          photoUrl: s.photoUrl || s.imageUrl || undefined,
          placeId: s.placeId,
          kbId: s.kbId,
          locationApproximate: s.locationApproximate,
          dayTrip: !!s.dayTrip,
          synthetic: !!s.synthetic,
          movedFrom: s.movedFrom,
          durationMin: s.durationMin,
        };
        if (act.synthetic && !act.description) {
          act.description = 'Where the day starts — your hotel or pickup point.';
        }
        if (d.dayTrip || act.synthetic) return act;
        const local = findLocal(s);
        if (!local) return act;
        if (act.time == null) act.time = local.time;
        if (act.description == null) act.description = local.description;
        if (act.cost == null) act.cost = local.cost;
        if (act.tip == null) act.tip = local.tip;
        if (act.photoUrl == null) act.photoUrl = local.photoUrl;
        if (act.placeId == null) act.placeId = local.placeId;
        if (act.kbId == null) act.kbId = local.kbId;
        if (act.locationApproximate == null) act.locationApproximate = local.locationApproximate;
        if (act.travel == null) act.travel = local.travel;
        return act;
      }),
    })),
  };
}

// Planner progress is stashed here before we bounce the user to sign-in, so they
// come back to their answers (and any generated itinerary) instead of a blank form.
const DRAFT_KEY = 'gb.aiPlanner.draft';
const GEN = PSTEPS.length;

const money = n => '$' + Math.round(n).toLocaleString();
const fmtDate = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const monthOffset = d => { const t = new Date(); return (d.getFullYear() - t.getFullYear()) * 12 + (d.getMonth() - t.getMonth()); };

const CSS = `
.aip { background: ${B.sandPatternSoft}; min-height: 100vh; }
.aip .wrap { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.aip .serif { font-family: 'Playfair Display', serif; }
.aip .page-head { padding: 40px 0 6px; }
.aip .kicker { font-size: 12px; letter-spacing: .26em; font-weight: 700; color: ${B.forest}; text-transform: uppercase; }
.aip .page-head h1 { font-size: 46px; font-weight: 800; margin-top: 6px; }
.aip .page-head p { color: ${B.muted}; margin-top: 8px; font-size: 16px; }

.aip .plan { display: grid; grid-template-columns: 1fr 340px; gap: 40px; align-items: start; padding: 36px 0 80px; }
.aip .plan-steps { min-width: 0; }
.aip .pstep { margin-bottom: 14px; }
.aip .pstep[data-state="hidden"] { display: none; }

.aip .collapsed { width: 100%; display: none; align-items: center; gap: 14px; cursor: pointer; padding: 15px 18px; border: 1.5px solid #e7e2d9; border-radius: 14px; background: #fff; transition: border-color .18s, box-shadow .18s, transform .18s; text-align: left; font-family: 'Montserrat', sans-serif; }
.aip .pstep[data-state="collapsed"] .collapsed { display: flex; animation: aipSettle .35s ease; }
@keyframes aipSettle { from { opacity: .4; transform: scale(.99); } to { opacity: 1; transform: none; } }
.aip .collapsed:hover { border-color: ${B.forest}; box-shadow: 0 2px 8px rgba(15,25,35,.06); }
.aip .collapsed:active { transform: scale(.995); }
.aip .collapsed .thumb { width: 46px; height: 46px; border-radius: 10px; object-fit: cover; flex: 0 0 auto; }
.aip .collapsed .step-mini { font-size: 11px; font-weight: 700; color: ${B.muted}; letter-spacing: .07em; text-transform: uppercase; }
.aip .collapsed .pick { font-weight: 700; font-size: 17px; margin-top: 2px; color: ${B.night}; }
.aip .collapsed .edit { margin-left: auto; color: ${B.forest}; font-weight: 700; font-size: 13px; white-space: nowrap; display: flex; align-items: center; gap: 5px; }

.aip .ex-wrap { display: grid; grid-template-rows: 0fr; opacity: 0; transition: grid-template-rows .42s cubic-bezier(.4,0,.2,1), opacity .3s ease; }
.aip .ex-inner { overflow: hidden; min-height: 0; }
.aip .pstep[data-state="expanded"] .ex-wrap, .aip .pstep[data-state="editing"] .ex-wrap { grid-template-rows: 1fr; opacity: 1; }
.aip .pstep[data-state="preopen"] .ex-wrap { grid-template-rows: 0fr; opacity: 0; }

.aip .num { font-size: 13px; font-weight: 700; color: ${B.forest}; letter-spacing: .04em; }
.aip .pstep h2 { font-size: 34px; font-weight: 800; margin: 6px 0 22px; line-height: 1.12; }

.aip .opt-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
.aip .opt { border: 2px solid #e7e2d9; background: #fff; border-radius: 12px; padding: 18px; text-align: left; transition: .18s; position: relative; cursor: pointer; font-family: 'Montserrat', sans-serif; color: ${B.night}; }
.aip .opt:hover { border-color: ${B.mint}; transform: translateY(-3px); box-shadow: 0 6px 20px rgba(15,25,35,.10); }
.aip .opt.on { border-color: ${B.forest}; box-shadow: 0 0 0 3px rgba(26,107,74,.12); }
.aip .opt.on::after { content: "✓"; position: absolute; top: 10px; right: 12px; width: 22px; height: 22px; background: ${B.forest}; color: #fff; border-radius: 50%; display: grid; place-items: center; font-size: 13px; font-weight: 700; }
.aip .opt h4 { font-size: 16px; margin-bottom: 3px; }
.aip .opt p { font-size: 12.5px; color: ${B.muted}; }
.aip .opt .em { font-size: 26px; margin-bottom: 8px; display: block; }
.aip .opt-dest { padding: 0; overflow: hidden; }
.aip .opt-dest img { width: 100%; height: 96px; object-fit: cover; }
.aip .opt-dest .t { padding: 10px 12px 12px; }
.aip .opt-dest .t h4 { font-size: 14px; }

.aip .btn-cont, .aip .btn-gen { font-family: 'Montserrat', sans-serif; font-weight: 700; border: none; cursor: pointer; background: ${B.forest}; color: #fff; border-radius: 999px; transition: .18s; }
.aip .btn-cont { margin-top: 20px; padding: 12px 24px; font-size: 14px; }
.aip .btn-cont:hover:not([disabled]) { filter: brightness(1.08); transform: translateY(-1px); }
.aip .btn-gen { padding: 15px 32px; font-size: 16px; }
.aip .btn-cont[disabled] { opacity: .4; cursor: not-allowed; }

/* calendar */
.aip .cal-dual { display: flex; gap: 24px; flex-wrap: wrap; }
.aip .cal { flex: 1; min-width: 288px; max-width: 360px; }
.aip .cal-label { font-weight: 700; font-size: 14px; margin-bottom: 12px; }
.aip .cal-ghost { border: 1.5px dashed #e7e2d9; border-radius: 14px; display: flex; flex-direction: column; padding: 20px; min-height: 300px; }
.aip .cal-ghost .cal-label { opacity: .5; }
.aip .cal-ghost-msg { margin: auto; color: ${B.muted}; font-size: 14px; text-align: center; line-height: 1.6; }
.aip .cal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.aip .cal-head b { font-size: 17px; }
.aip .cal-nav { width: 36px; height: 36px; border-radius: 50%; border: 1.5px solid #e7e2d9; background: #fff; font-size: 18px; line-height: 1; color: ${B.night}; cursor: pointer; transition: .16s; }
.aip .cal-nav:hover:not([disabled]) { border-color: ${B.forest}; color: ${B.forest}; }
.aip .cal-nav[disabled] { opacity: .3; cursor: not-allowed; }
.aip .cal-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 6px; }
.aip .cal-dow { margin-bottom: 8px; }
.aip .cal-dow span { text-align: center; font-size: 11px; font-weight: 700; color: ${B.muted}; }
.aip .cal-cell { aspect-ratio: 1; min-width: 0; min-height: 0; background: #fff; border: 1.5px solid transparent; border-radius: 10px; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 600; color: ${B.night}; cursor: pointer; transition: .14s; }
.aip .cal-cell:hover:not(.past):not(.empty) { background: rgba(26,107,74,.1); border-color: ${B.forest}; }
.aip .cal-cell.past { color: #c7ccd1; cursor: not-allowed; }
.aip .cal-cell.empty { visibility: hidden; cursor: default; }
.aip .cal-cell.sel { background: ${B.forest}; color: #fff; border-color: ${B.forest}; }
.aip .cal-cell.inrange { background: rgba(26,107,74,.12); }
.aip .flex-chip { margin-top: 14px; border: 1.5px dashed #e7e2d9; background: #fff; border-radius: 999px; padding: 10px 18px; font-family: 'Montserrat', sans-serif; font-weight: 600; font-size: 14px; cursor: pointer; color: ${B.night}; }
.aip .flex-chip:hover { border-color: ${B.forest}; color: ${B.forest}; }
.aip .flex-days { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 12px; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 600; color: ${B.night}; }
.aip .flex-days button { width: 34px; height: 34px; border-radius: 50%; border: 1.5px solid ${B.forest}; background: #fff; color: ${B.forest}; font-size: 18px; font-weight: 700; cursor: pointer; line-height: 1; }
.aip .flex-days button:disabled { opacity: .35; cursor: default; }
.aip .flex-days select { padding: 7px 10px; border-radius: 10px; border: 1.5px solid #e7e2d9; font-family: inherit; font-size: 14px; font-weight: 600; background: #fff; }

/* summary rail */
.aip .summary { position: sticky; top: 84px; background: ${B.night}; color: #fff; border-radius: 20px; overflow: hidden; box-shadow: 0 16px 40px rgba(15,25,35,.16); }
.aip .summary .top { height: 130px; position: relative; background: #16222e; }
.aip .summary .top img { width: 100%; height: 100%; object-fit: cover; opacity: .9; }
.aip .summary .bd { padding: 20px; }
.aip .summary h3 { font-size: 18px; margin-bottom: 14px; font-family: 'Playfair Display', serif; }
.aip .sum-row { display: flex; justify-content: space-between; gap: 10px; font-size: 13px; padding: 9px 0; border-bottom: 1px solid rgba(255,255,255,.08); }
.aip .sum-row span:first-child { color: #9fb0bd; }
.aip .sum-row span:last-child { font-weight: 700; text-align: right; }
.aip .sum-total { display: flex; justify-content: space-between; align-items: baseline; margin: 16px 0 14px; }
.aip .sum-total b { font-size: 26px; color: ${B.mint}; }
.aip .cost-note { margin: -8px 0 14px; font-size: 11px; color: ${B.muted}; font-family: 'Montserrat', sans-serif; }
.aip .cost-note-row { display: flex; justify-content: space-between; gap: 10px; line-height: 1.7; }
.aip .cost-breakdown { margin: 6px 0 4px; padding: 6px 0; border-top: 1px dashed rgba(255,255,255,.12); border-bottom: 1px dashed rgba(255,255,255,.12); color: #c9d4dc; }
.aip .cost-note-src { display: block; color: ${B.muted}; opacity: .85; font-size: 10.5px; }
.aip .cost-scope { color: #b8c4cc; opacity: 1; font-size: 11.5px; }
.aip .sum-gen { width: 100%; background: ${B.forest}; color: #fff; border: none; border-radius: 12px; padding: 14px 0; font-weight: 700; font-size: 14px; cursor: pointer; font-family: 'Montserrat', sans-serif; }
.aip .sum-gen[disabled] { opacity: .4; cursor: not-allowed; }
.aip .progress-dots { display: flex; gap: 6px; padding: 0 20px 16px; }
.aip .pdot { height: 4px; flex: 1; background: rgba(255,255,255,.15); border-radius: 3px; transition: .3s; }
.aip .pdot.on { background: ${B.mint}; }

/* itinerary */
.aip .day-grid { display: grid; gap: 4px; padding: 4px; background: #efebe4; border-radius: 14px; }
.aip .day-grid button { min-width: 0; padding: 9px 4px; border: none; border-radius: 10px; background: transparent; color: ${B.muted}; font: 600 13px 'Montserrat', sans-serif; cursor: pointer; white-space: nowrap; transition: background .15s, color .15s; }
.aip .day-grid button:hover { color: ${B.forest}; }
.aip .day-grid button.on { background: #fff; color: ${B.forest}; font-weight: 800; box-shadow: 0 2px 8px rgba(0,0,0,.1); }
.aip .detail-hero { position: relative; height: 300px; overflow: hidden; }
.aip .detail-hero img { width: 100%; height: 100%; object-fit: cover; }
.aip .detail-hero .ov { position: absolute; inset: 0; background: linear-gradient(180deg,rgba(0,0,0,.1),rgba(0,0,0,.7)); }
.aip .detail-hero .info { position: absolute; left: 0; right: 0; bottom: 0; padding: 28px; color: #fff; z-index: 2; }
.aip .detail-hero .info h1 { font-size: 38px; font-weight: 800; }
.aip .back { display: inline-flex; align-items: center; gap: 8px; color: ${B.forest}; background: #fff; padding: 12px 22px; border: 2px solid ${B.forest}; border-radius: 999px; font-size: 15px; font-weight: 800; position: absolute; top: 18px; left: 18px; z-index: 3; cursor: pointer; font-family: 'Montserrat', sans-serif; box-shadow: 0 6px 18px rgba(15,25,35,.28); transition: .18s; }
.aip .back:hover { background: ${B.forest}; color: #fff; transform: translateY(-1px); }
.aip .chip-tag { background: rgba(255,255,255,.92); padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; color: ${B.night}; display: inline-block; }
.aip .stat-row { display: flex; gap: 14px; flex-wrap: wrap; margin: 24px 0; }
.aip .stat { background: #fff; border: 1px solid #e7e2d9; border-radius: 12px; padding: 14px 20px; flex: 1; min-width: 120px; }
.aip .stat b { display: block; font-size: 22px; }
.aip .stat span { font-size: 12px; color: ${B.muted}; font-weight: 600; }
.aip .day { background: #fff; border: 1px solid #e7e2d9; border-radius: 20px; padding: 20px 22px; margin-bottom: 16px; }
.aip .day .dh { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.aip .day .badge { width: 42px; height: 42px; border-radius: 12px; background: ${B.forest}; color: #fff; display: grid; place-items: center; font-weight: 800; flex: 0 0 auto; }
.aip .day .badge small { font-size: 9px; font-weight: 600; opacity: .8; display: block; line-height: 1; }
.aip .act { display: flex; gap: 14px; padding: 12px 0; border-top: 1px dashed #e7e2d9; }
.aip .act:first-of-type { border-top: none; }
.aip .act .time { font-size: 12px; font-weight: 700; color: ${B.forest}; width: 74px; flex: 0 0 auto; padding-top: 2px; }
.aip .act h5 { font-size: 15px; margin-bottom: 2px; }
.aip .act p { font-size: 13px; color: ${B.muted}; }
.aip .it-btn { font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 14px; border-radius: 999px; padding: 12px 24px; cursor: pointer; }
.aip .it-forest { background: ${B.forest}; color: #fff; border: none; }
.aip .it-out { background: #fff; color: ${B.forest}; border: 1.5px solid ${B.forest}; }

@media (max-width: 900px) {
  .aip .plan { grid-template-columns: 1fr; }
  .aip .opt-grid { grid-template-columns: repeat(2,1fr); }
  .aip .pstep h2 { font-size: 26px; }
  .aip .page-head h1 { font-size: 32px; }
  .aip .summary { position: static; }
}
@media (max-width: 768px) {
  /* Phone: one page scroll like Google Maps — compact map on top (expand button
     for full screen), every stop listed below it. No inner scroll box. */
  .aip .split-pane { flex-direction: column !important; min-height: 0 !important; max-height: none !important; height: auto !important; overflow: visible !important; }
  .aip .split-left { width: 100% !important; border-right: none !important; max-height: none !important; overflow: visible !important; flex: 0 0 auto !important; min-height: 0 !important; padding: 16px 14px calc(88px + env(safe-area-inset-bottom)) !important; }
  .aip .split-right { order: -1; width: 100% !important; height: auto !important; min-height: 0 !important; flex-shrink: 0 !important; position: static !important; border-radius: 12px 12px 0 0 !important; border-bottom: 1px solid #e5e0d8; }
  .aip .split-left { border-radius: 0 0 12px 12px !important; }
  .aip .gb-map--compact .leaflet-container { touch-action: pan-x pan-y !important; }
  .aip .detail-hero .info h1 { font-size: clamp(22px, 5vw, 38px) !important; }
  .aip .detail-hero { height: 220px !important; }
  .aip .stat-row { gap: 8px !important; }
  .aip .stat { min-width: 80px !important; padding: 10px 12px !important; }
  .aip .stat b { font-size: 16px !important; }
  .aip .itin-actions { flex-direction: column !important; }
  .aip .itin-actions .it-btn { width: 100% !important; text-align: center !important; }
  .aip .itin-stats-bottom { flex-direction: column !important; }
  .aip .day-tabs-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
}
@media (max-width: 480px) {
  .aip .opt-grid { grid-template-columns: 1fr !important; }
  .aip .stat-row { flex-direction: column !important; }
  .aip .stat { min-width: 0 !important; }
}
`;

const DEST_LIMIT = 10;

const PHONE_QUERY = '(max-width: 768px)';
function usePhone() {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches;
  const [phone, setPhone] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const on = () => setPhone(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  return phone;
}

export default function AIAccordionScreen({ goScreen, prefillDestination, prefillDays, editTripId, user, onAuthRequired }) {
const [plan, setPlan] = useState({
  dest: null, startDate: null, endDate: null, flexible: false,
  duration: null, pace: 'balanced', budgetMult: 1, budgetLabel: 'Comfort', who: 1, whoLabel: 'Solo', transport: 'car',
  includeDayTrips: false,
});
  const [states, setStates] = useState(() => { const a = Array(GEN).fill('hidden'); a[0] = 'expanded'; return a; });
  const [calStartOffset, setCalStartOffset] = useState(0);
  const [calEndOffset, setCalEndOffset] = useState(0);
  const [view, setView] = useState('plan'); // plan | loading | itinerary
  const isPhone = usePhone();
  const [genError, setGenError] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [meta, setMeta] = useState({});
  // Day-construction model. Must match the backend default in routes/ai.js, which is
  // now 'anchor' — this screen ALWAYS sends `model`, so a mismatch here would silently
  // override the server default and nobody would see the change.
  const [model, setModel] = useState('anchor');
  const [destList, setDestList] = useState([]);
  const [destSearch, setDestSearch] = useState('');
  const [destCount, setDestCount] = useState(DEST_LIMIT);
  const [activeDay, setActiveDay] = useState(0);
  const [mapLocations, setMapLocations] = useState([]);
  const [hoveredActivity, setHoveredActivity] = useState(null);
  // Verified place (by Place row id) whose PlaceSheet is open from an activity card.
  const [openPlace, setOpenPlace] = useState(null);
  const [showRefine, setShowRefine] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);
  // Refinement is a COMPARISON, not a replacement. `baseline` holds the plan as
  // it was before any refinement; `generated` is the refined one being shown.
  // Further refinements stack on the refined plan, and the user keeps whichever
  // they prefer — the original is never overwritten until they say so.
  const [baseline, setBaseline] = useState(null);
  const [refineLog, setRefineLog] = useState([]);
  const [showBaseline, setShowBaseline] = useState(false);
  // Day-trips rail + swap flow. `dayTrips` is the catalog for the base city;
  // `dayTripPlans` holds all accepted day-trip swaps in order; each is applied
  // sequentially on the live itinerary. Undo clears all active day trips.
  const [dayTrips, setDayTrips] = useState([]);
  const [dayTripPlans, setDayTripPlans] = useState([]);
  const [swapTarget, setSwapTarget] = useState(null);
  const [swapDayNumber, setSwapDayNumber] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapError, setSwapError] = useState(null);
  const stepRefs = useRef({});

  const geocodeItinerary = async (itinerary) => {
    const locations = [];
    for (const day of itinerary.days || []) {
      const activities = day.activities || [];
      for (let i = 0; i < activities.length; i++) {
        const act = activities[i];
        if (act.lat && act.lng) {
          locations.push({ lat: act.lat, lng: act.lng, dayNumber: day.day, activityIndex: i, title: act.activity, description: act.description });
        } else if (act.description) {
          const geo = await geocodeWithCache(act.description);
          if (geo) locations.push({ ...geo, dayNumber: day.day, activityIndex: i, title: act.activity, description: act.description });
        }
      }
    }
    return locations;
  };

  // Persist / restore planner progress across a sign-in round trip (the app
  // remounts this screen when it navigates to the auth screen and back).
  const persistDraft = useCallback(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
        plan: {
          ...plan,
          startDate: plan.startDate ? plan.startDate.toISOString() : null,
          endDate: plan.endDate ? plan.endDate.toISOString() : null,
        },
        states,
        view: view === 'loading' ? 'plan' : view,
        generated, meta, activeDay, mapLocations,
      }));
    } catch { /* storage unavailable (private mode / quota) — progress just isn't kept */ }
  }, [plan, states, view, generated, meta, activeDay, mapLocations]);

  const requireAuth = useCallback(() => {
    persistDraft();
    onAuthRequired?.();
  }, [persistDraft, onAuthRequired]);

  // With no explicit destination, reopen on the one picked last in this tab.
  const [storedDest] = useState(() => (prefillDestination || editTripId ? null : loadSelectedDestination()));
  useEffect(() => {
    if (plan.dest) saveSelectedDestination(plan.dest);
  }, [plan.dest]);

  const draftRestored = useRef(false);
  useEffect(() => {
    let raw = null;
    try {
      raw = sessionStorage.getItem(DRAFT_KEY);
      sessionStorage.removeItem(DRAFT_KEY);
    } catch { /* ignore */ }
    if (!raw) return;
    try {
      const draft = JSON.parse(raw);
      if (draft.plan) {
        setPlan(p => ({
          ...p, ...draft.plan,
          startDate: draft.plan.startDate ? new Date(draft.plan.startDate) : null,
          endDate: draft.plan.endDate ? new Date(draft.plan.endDate) : null,
        }));
      }
      if (Array.isArray(draft.states) && draft.states.length === GEN) setStates(draft.states);
      // Drafts saved before the local sample plan was removed can still hold it;
      // never bring that made-up plan back.
      if (draft.generated?.days?.length && !draft.meta?.fallback && !draft.meta?.mock) {
        setGenerated(draft.generated);
        setMeta(draft.meta || {});
        setActiveDay(draft.activeDay || 0);
        setMapLocations(draft.mapLocations || []);
        if (draft.view === 'itinerary') setView('itinerary');
      }
      draftRestored.current = true;
    } catch { /* corrupt draft — start fresh */ }
  }, []);

  // Edit mode (My Trips → Edit on an AI trip): load the saved trip into the
  // itinerary view; Save then updates that trip instead of creating a new one.
  const editLoaded = useRef(null);
  useEffect(() => {
    if (!editTripId || editLoaded.current === editTripId) return;
    editLoaded.current = editTripId;
    draftRestored.current = true;
    tripsAPI.get(editTripId).then(async (t) => {
      if (!t || !t.days) return;
      const parseNotes = (n) => { try { const j = JSON.parse(n); return j && typeof j === 'object' ? j : null; } catch { return null; } };
      const itin = {
        title: t.title,
        days: t.days.map((d) => ({
          day: d.dayNumber,
          title: d.title || '',
          activities: (d.activities || []).map((a) => {
            const meta = parseNotes(a.notes);
            return {
              name: a.name,
              activity: a.name,
              time: a.time || '',
              location: a.location || '',
              placeId: a.placeId || '',
              lat: meta && typeof meta._lat === 'number' ? meta._lat : a.lat ?? undefined,
              lng: meta && typeof meta._lng === 'number' ? meta._lng : a.lng ?? undefined,
              photoUrl: (meta && meta.photoUrl) || '',
              locationApproximate: meta ? meta._approx : undefined,
              description: meta ? (meta.description || '') : (a.notes || ''),
            };
          }),
        })),
      };
      const start = t.startDate ? new Date(t.startDate) : null;
      const end = t.endDate ? new Date(t.endDate) : null;
      setPlan((p) => ({
        ...p,
        dest: { name: t.destination, img: t.coverImage || '', imgLg: t.coverImage || '' },
        startDate: start,
        endDate: end,
        flexible: !start,
        duration: itin.days.length,
      }));
      setGenerated(itin);
      setSavedTrip({ source: itin, trip: t });
      setActiveDay(0);
      setView('itinerary');
      setMapLocations(await geocodeItinerary(itin));
    }).catch((err) => console.error('Failed to load trip for editing:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTripId]);

  useEffect(() => {
    destinationsAPI.list()
      .then(data => setDestList(Array.isArray(data) ? data : data.destinations || []))
      .catch(() => setDestList([]));
  }, []);

  // Home city for /destinations/daytrips: the picked destination is a real city
  // row (gb:city:<id>), so the numeric cityId is the day trips' home.
  const homeCityId = (() => {
    const p = plan.dest;
    if (!p) return null;
    if (Number.isInteger(p.cityId) && p.cityId > 0) return p.cityId;
    const m = /^gb:city:(\d+)$/.exec(String(p.placeId || p.id || ''));
    return m ? Number(m[1]) : null;
  })();

  useEffect(() => {
    if (view !== 'itinerary' || homeCityId == null) { setDayTrips([]); return undefined; }
    setDayTrips([]);
    let cancelled = false;
    destinationsAPI.daytrips(homeCityId)
      .then(r => { if (!cancelled) setDayTrips((r && r.dayTrips) || []); })
      .catch(() => { if (!cancelled) setDayTrips([]); });
    return () => { cancelled = true; };
  }, [view, homeCityId]);

  // Auto-select destination when user arrives from search/global search
  useEffect(() => {
    const pd = prefillDestination || storedDest;
    if (!pd || draftRestored.current) return;
    const withDest = (p, dest) => ({
      ...p, dest,
      ...(prefillDays ? { flexible: true, startDate: null, endDate: null, duration: prefillDays } : {}),
    });
    // If prefillDestination is a full destination object (from GlobalSearch), use it directly
    if (typeof pd === 'object' && pd.name) {
      setPlan(p => withDest(p, pd));
      setStepState(0, 'collapsed');
      setTimeout(() => reveal(1), 50);
      return;
    }
    // Otherwise it's a name string — try to match against the loaded destination list
    if (destList.length === 0) return;
    const want = pd.split(',')[0].trim().toLowerCase();
    const match = destList.find(d => d.name.toLowerCase() === want)
      || destList.find(d => d.name.toLowerCase().includes(want));
    const apply = (dest) => {
      setPlan(p => withDest(p, dest));
      setStepState(0, 'collapsed');
      setTimeout(() => reveal(1), 50);
    };
    if (match) { apply(match); return; }
    // Not in the popular list (top 200): fall back to the planner's own plannable search.
    let cancelled = false;
    destinationsAPI.search(want, { plannable: true })
      .then((r) => {
        const list = Array.isArray(r) ? r : (r?.results || r?.destinations || []);
        const hit = list.find(d => String(d.name || '').toLowerCase().startsWith(want)) || list[0];
        if (!cancelled && hit) apply(hit);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [prefillDestination, prefillDays, destList, storedDest]);

  // develop's shared hook (debounce, 2-char gate, cache, empty-query popular list) with
  // THIS branch's semantics preserved via the fetcher: the planner picker must offer only
  // cities we hold enough gated places to plan. Without `plannable: true` an unplannable
  // destination becomes selectable again and falls through to the ungrounded free-text
  // path -- the bug this branch fixed. The hook is endpoint-agnostic by design, so the
  // refactor and the filter are not in conflict.
  const { results: destResults, loading: destSearching } = useDebouncedDestSearch(destSearch, {
    fetcher: (q) => destinationsAPI.search(q, { plannable: true }),
  });

  const selectDestResult = (i, dest) => {
    setDestSearch('');
    chooseDest(i, dest);
  };

  const setStepState = (i, s) => setStates(prev => { const a = [...prev]; a[i] = s; return a; });
  const scrollToStep = i => setTimeout(() => { const el = stepRefs.current[i]; if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 120);

  const reveal = useCallback(next => {
    if (next >= GEN) return;
    setStates(prev => {
      if (prev[next] !== 'hidden') return prev;
      const a = [...prev]; a[next] = 'preopen';
      setTimeout(() => {
        setStates(p => { const b = [...p]; if (b[next] === 'preopen') b[next] = 'expanded'; return b; });
        scrollToStep(next);
      }, 30);
      return a;
    });
  }, []);

  const planReady = () => plan.dest && plan.pace && plan.budgetMult && plan.who;
  const transportIdx = PSTEPS.findIndex(s => s.key === 'transport');
  const transportStepOpen = states[transportIdx] === 'expanded' || states[transportIdx] === 'editing';

  // no separate generate step — the button is on the travelers step

  const openEdit = i => {
    setStates(prev => prev.map((s, idx) => (idx === i ? 'editing' : (s === 'editing' ? 'collapsed' : s))));
    scrollToStep(i);
  };

  const chooseDest = (i, d) => {
    setPlan(p => ({ ...p, dest: d }));
    setStepState(i, 'collapsed');
    reveal(i + 1);
  };

  const chooseStart = date => {
    setPlan(p => {
      let endDate = p.endDate, duration = p.duration;
      if (endDate && endDate <= date) { endDate = null; duration = null; }
      return { ...p, flexible: false, startDate: date, endDate, duration };
    });
    const nextDay = new Date(date); nextDay.setDate(nextDay.getDate() + 1);
    setCalEndOffset(monthOffset(nextDay));
  };
  const chooseReturn = (date, i) => {
    if (!plan.startDate || date <= plan.startDate) return;
    const duration = Math.round((date - plan.startDate) / 86400000) + 1;
    setPlan(p => ({ ...p, endDate: date, duration }));
    setStates(prev => { const a = [...prev]; a[i] = 'collapsed'; for (let j = i + 1; j < GEN; j++) if (a[j] === 'hidden') a[j] = 'collapsed'; return a; });
  };
  const chooseFlexible = i => {
    setPlan(p => ({ ...p, flexible: true, startDate: null, endDate: null, duration: 7 }));
    setStates(prev => { const a = [...prev]; a[i] = 'collapsed'; for (let j = i + 1; j < GEN; j++) if (a[j] === 'hidden') a[j] = 'collapsed'; return a; });
  };

  const setFlexibleDays = n => {
    const duration = Math.min(14, Math.max(1, Math.round(n) || 7));
    setPlan(p => ({ ...p, flexible: true, startDate: null, endDate: null, duration }));
  };

  const chooseOpt = (i, o) => {
    const s = PSTEPS[i];
    if (s.key === 'pace') setPlan(p => ({ ...p, pace: o.v }));
    if (s.key === 'budget') setPlan(p => ({ ...p, budgetMult: o.v, budgetLabel: o.sum }));
    if (s.key === 'who') setPlan(p => ({ ...p, who: o.v, whoLabel: o.sum }));
    if (s.key === 'transport') setPlan(p => ({ ...p, transport: o.v }));
    if (s.key === 'interests') {
      setPlan(p => {
        const cur = p.interests || [];
        return { ...p, interests: cur.includes(o.v) ? cur.filter(x => x !== o.v) : [...cur, o.v] };
      });
    }
  };
  const commitOpt = i => {
    if ((PSTEPS[i].key === 'budget' && !plan.budgetMult) || (PSTEPS[i].key === 'who' && !plan.who) || (PSTEPS[i].key === 'pace' && !plan.pace)) return;
    if (PSTEPS[i].key === 'transport' && !plan.transport) return;
    trackActivity('planner', 'plan_step_completed', { step: PSTEPS[i].key, tripId: null });
    if (PSTEPS[i].key === 'transport') { setStepState(i, 'collapsed'); return; }
    setStepState(i, 'collapsed');
    if (i + 1 < GEN) reveal(i + 1);
  };

  const dateSummary = () => {
    if (plan.flexible) return `Flexible · ${plan.duration || 7} ${(plan.duration || 7) === 1 ? 'day' : 'days'}`;
    if (!plan.startDate) return '—';
    if (!plan.endDate) return fmtDate(plan.startDate) + ' – select return';
    return `${fmtDate(plan.startDate)} – ${fmtDate(plan.endDate)} · ${plan.duration} days`;
  };
  const pickText = s => {
    switch (s.key) {
      case 'dest': return plan.dest ? plan.dest.name.split(',')[0] : '—';
      case 'dates': return dateSummary();
      case 'pace': return PSTEPS.find(x => x.key === 'pace')?.opts?.find(o => o.v === plan.pace)?.sum || '—';
      case 'budget': return plan.budgetLabel || '—';
      case 'who': return plan.whoLabel || '—';
      case 'transport': return PSTEPS.find(x => x.key === 'transport')?.opts?.find(o => o.v === plan.transport)?.sum || 'Transit';
      case 'interests': {
        const picked = (plan.interests || []).map(v => s.opts.find(o => o.v === v)?.sum).filter(Boolean);
        return picked.length ? picked.join(', ') : 'Anything';
      }
      default: return '—';
    }
  };
  // Trip cost comes from the server, which is the only place the party model lives.
  //
  // This used to be `dest.budget * (duration / dest.days) * budgetMult * who`, which was
  // wrong three ways at once: `dest.days` is always null so the first factor was NaN;
  // `budgetMult` was applied on top of a figure already chosen at a tier; and scaling one
  // traveller's day by headcount assumes cost is strictly per-person, when four people book
  // two rooms and three paying sight-seers. lib/tripBudget's PARTY table already encodes
  // that, so the arithmetic moved there rather than being re-guessed here.
  //
  // The planner's option values line up with it exactly: budgetMult is 0.7/1/1.8 (Budget /
  // Comfort / Luxury), who is 1/2/4/6, pace is relaxed|balanced|packed, transport is
  // foot|transit|rideshare|car. Nothing needs translating, which is why this can pass through.
  // Best-time fallback when the LLM leaves bestTimeToVisit empty: the city's own bestMonths.
  const [cityBestMonths, setCityBestMonths] = useState(null);
  useEffect(() => {
    setCityBestMonths(null);
    if (homeCityId == null || !destinationsAPI.overview) return undefined;
    let cancelled = false;
    Promise.resolve(destinationsAPI.overview(homeCityId))
      .then(r => { if (!cancelled) setCityBestMonths(monthRanges(r && r.bestMonths)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [homeCityId]);

  const [cost, setCost] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const costCityId = plan.dest?.cityId ?? null;

  useEffect(() => {
    if (!costCityId || !plan.duration) { setCost(null); return undefined; }
    let cancelled = false;
    setCostLoading(true);
    destinationsAPI.estimate({
      cityId: costCityId,
      style: plan.budgetMult,
      days: plan.duration,
      travelers: plan.who,
      pace: plan.pace,
      transport: plan.transport,
    })
      .then((r) => { if (!cancelled) setCost(r); })
      .catch(() => { if (!cancelled) setCost(null); })
      .finally(() => { if (!cancelled) setCostLoading(false); });
    return () => { cancelled = true; };
  }, [costCityId, plan.budgetMult, plan.duration, plan.who, plan.pace, plan.transport]);

  // null means "we cannot price this", which is different from 0. Callers must not coerce it.
  const estPrice = () => (cost && cost.hasData && Number.isFinite(cost.total) ? cost.total : null);
  // Server-side party breakdown (lib/tripBudget); only parts that actually cost something.
  const costBreakdownRows = () => {
    const b = cost && cost.breakdown;
    if (!b) return [];
    return [
      ['Stay', b.lodging],
      ['Getting around', b.local],
      [plan.transport === 'car' ? 'Car (rental, fuel, parking)' : 'Car', b.car],
      ['Sights & tickets', b.sights],
    ].filter(([, v]) => Number.isFinite(v) && v > 0);
  };
  const provenanceLabel = () => {
    const d = cost?.detail;
    if (!d || !d.provenance) return null;
    if (d.provenance.level === 'parsed') return 'from published prices';
    if (d.provenance.level === 'mixed') return 'partly measured, partly modelled';
    if (d.provenance.level === 'estimated') return 'modelled from nearby cities';
    return null;
  };
  const [saving, setSaving] = useState(false);
  // The last trip row created from an on-screen itinerary, so Share reuses it instead of
  // saving a duplicate every click. Keyed by itinerary object: a refine or swap is a new plan.
  const [savedTrip, setSavedTrip] = useState(null);
  const [shareTrip, setShareTrip] = useState(null);
  const [sharePrompt, setSharePrompt] = useState(null);
  const [shareError, setShareError] = useState(null);
  const persistItinerary = async (itin) => {
    if (savedTrip && savedTrip.source === itin) return savedTrip.trip;
    const d = plan.dest;
    const img = d.img || d.imgLg || '';
    const priced = estPrice();
    const payload = {
      source: 'AI',
      destination: d.name,
      title: itin.title || `${plan.duration || itin.days?.length} Days in ${d.name.split(',')[0]}`,
      startDate: plan.startDate?.toISOString(),
      endDate: plan.endDate?.toISOString(),
      // Omitted rather than written as 0 while the estimate is still in flight or when the
      // city has no priced component. Trip.budget is a number the app reads back as this
      // trip's budget, and 0 is a claim that the trip is free.
      ...(priced != null ? { budget: Math.round(priced) } : {}),
      ...(img ? { coverImage: img } : {}),
      days: itin.days?.map(day => ({
        dayNumber: day.day,
        title: day.title || '',
        activities: day.activities?.map(act => ({
          name: act.name || act.activity || '',
          time: act.time || '',
          location: act.location || act.activity || act.name || '',
          placeId: act.placeId || '',
          notes: act.lat != null && act.lng != null
            ? JSON.stringify({ _lat: act.lat, _lng: act.lng, photoUrl: act.photoUrl || '', _approx: act.locationApproximate || undefined, description: (act.description || '').slice(0, 1500) })
            : (act.description || '').slice(0, 2000),
        })) || [],
      })) || [],
    };
    const trip = editTripId ? await tripsAPI.update(editTripId, payload) : await tripsAPI.create(payload);
    setSavedTrip({ source: itin, trip });
    return trip;
  };
  const saveItinerary = async () => {
    if (!user) { requireAuth(); return; }
    if (!itin || !plan.dest) return;
    setSaving(true);
    try {
      await persistItinerary(itin);
      goScreen('trips');
    } catch (err) {
      console.error('Failed to save trip:', err);
    } finally {
      setSaving(false);
    }
  };
  const shareItinerary = (itin) => {
    if (!user) { requireAuth(); return; }
    if (!itin || !plan.dest) return;
    const defaultMsg = `Check out my trip to ${plan.dest.name ? plan.dest.name.split(',')[0] : ''}!`;
    setShareError(null);
    setSharePrompt({ itin, message: defaultMsg });
  };

  const confirmShareItinerary = async () => {
    if (!sharePrompt || !sharePrompt.itin) return;
    const finalMsg = (sharePrompt.message || '').trim() || `Check out my trip to ${plan.dest.name ? plan.dest.name.split(',')[0] : ''}!`;
    setShareError(null);
    setSaving(true);
    try {
      const trip = await persistItinerary(sharePrompt.itin);
      if (!trip || trip.id == null) throw new Error('Trip was not saved');
      let isPublic = Boolean(trip.isPublic);
      try {
        await tripsAPI.share(trip.id);
        isPublic = true;
      } catch (err) {
        console.error('Failed to make trip public:', err);
      }
      setSharePrompt(null);
      setShareTrip({ ...trip, isPublic, __shareText: finalMsg });
    } catch (err) {
      console.error('Share error:', err);
      setShareError('Could not share this trip. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const answeredCount = () => {
    let n = 0;
    if (plan.dest) n++;
    if ((plan.startDate && plan.endDate) || plan.flexible) n++;
    if (plan.pace) n++;
    if (plan.budgetMult) n++;
    if (plan.who) n++;
    return n;
  };

  const generate = async (modelOverride) => {
    // Explicit override rather than reading state: setModel is async, so a
    // re-plan triggered straight after choosing a model would use the old one.
    const useModel = typeof modelOverride === 'string' ? modelOverride : model;
    if (!plan.dest) return;
    if (!user) { requireAuth(); return; }
    setView('loading');
    setGenError(null);
    window.scrollTo(0, 0);
    // A swap was made against the previous plan — a new plan is a new contract.
    setDayTripPlans([]);
    setSwapTarget(null);
    setSwapDayNumber(null);
    setSwapLoading(false);
    setSwapError(null);
    // Default to 7 days when no dates are set so the button works without dates.
    const duration = plan.duration || 7;
    try {
      const result = await ai.generateItinerary({
        destination: plan.dest.name,
        // Our own city id ("gb:city:<id>"), so the backend resolves the exact row
        // the user picked instead of re-matching the name — no geocode, no
        // ambiguity between same-named cities.
        placeId: plan.dest.placeId || plan.dest.id || undefined,
        days: duration,
        // Budget TIER (Budget/Comfort/Luxury) — drives restaurant price selection
        // (and hotels later). Reliable signal vs a coarse total estimate.
        budget: plan.budgetLabel || 'flexible',
        pace: plan.pace,
        travelers: plan.who || 1,
        transport: plan.transport || 'transit',
        interests: (plan.interests || []).length ? plan.interests.join(', ') : undefined,
        model: useModel,
      });
      if (!result?.itinerary?.days?.length || result.mock) throw new Error('Empty itinerary');
      // Pin to the deterministic CityCost figure shown on the plan page so the
      // header matches; keep the LLM estimate when the city has no priced data.
      const priced = estPrice();
      if (priced != null) result.itinerary.totalBudgetEstimate = money(priced);
      setGenerated(result.itinerary);
      trackActivity('planner', 'trip_plan_generated', { mode: 'ai', destination: plan?.dest?.name ?? null, tripId: null });
      setMeta({ grounded: !!result.grounded, mock: !!result.mock, cached: !!result.cached, });
      setActiveDay(0);
      const newLocations = await geocodeItinerary(result.itinerary);
      setMapLocations(newLocations);
      setView('itinerary');
    } catch (err) {
      // No made-up stand-in plan: only itineraries built from the city's real
      // places are shown. Back to the plan with the reason and a retry.
      console.warn('Itinerary request failed:', err);
      setGenError(err?.status === 401
        ? 'Your session ended. Please sign in again to generate your itinerary.'
        : "We couldn't build your itinerary from verified places right now. Please try again.");
      if (err?.status === 401) requireAuth();
      setView('plan');
    }
    window.scrollTo(0, 0);
  };

  // Show the original or the refined plan, re-pointing the map at whichever it
  // is — otherwise the pins keep belonging to the other plan.
  const viewPlan = async (wantBaseline) => {
    if (dayTripPlans.length > 0 || wantBaseline === showBaseline) return;
    setShowBaseline(wantBaseline);
    const shown = wantBaseline ? baseline : generated;
    if (!shown) return;
    setActiveDay(0);
    const locs = await geocodeItinerary(shown);
    setMapLocations(locs);
  };

  // Keep the refined plan: it becomes the new original and the comparison ends.
  const keepRefined = () => {
    if (dayTripPlans.length > 0) return;
    setBaseline(null);
    setRefineLog([]);
    setShowBaseline(false);
  };

  // Discard every refinement and go back to the plan as first generated.
  const keepOriginal = async () => {
    if (dayTripPlans.length > 0 || !baseline) return;
    const restored = baseline;
    setGenerated(restored);
    setBaseline(null);
    setRefineLog([]);
    setShowBaseline(false);
    setActiveDay(0);
    const locs = await geocodeItinerary(restored);
    setMapLocations(locs);
  };

  // Open the "which day gives way" sheet for a day trip. Default = the day with
  // the fewest stops, so the swap disturbs the least of the plan.
  const openSwap = dt => {
    const days = itin.days || [];
    let def = days[0]?.day || 1;
    let min = Infinity;
    days.forEach((d, i) => {
      const n = (d.activities || []).length;
      if (n < min) { min = n; def = d.day || i + 1; }
    });
    setSwapDayNumber(def);
    setSwapTarget(dt);
    setSwapError(null);
  };

  // Build the swap payload from the LIVE itinerary and run the repack. The pace
  // is the plan's own chosen pace, so the re-packed days share the on-screen
  // plan's budgets.
  const confirmSwap = async () => {
    const isAttraction = swapTarget && swapTarget.targetPlaceId != null;
    if (!swapTarget || (!isAttraction && swapTarget.cityId == null) || !itin.days) return;
    const payload = {
      destination: plan.dest.name,
      placeId: isAttraction ? `gb:place:${swapTarget.targetPlaceId}` : `gb:city:${swapTarget.cityId}`,
      home: { lat: plan.dest.lat, lng: plan.dest.lng },
      pace: plan.pace,
      transport: plan.transport || 'transit',
      // The day-construction model the plan was built with (Landmark-led / Balanced
      // / Time-budgeted), so the re-pack uses the SAME model's objective.
      model,
      ...(Number.isInteger(swapTarget.travelMinutes) && swapTarget.travelMinutes > 0
        ? { travelMinutes: swapTarget.travelMinutes }
        : {}),
      replaceDayNumber: swapDayNumber,
      days: itin.days.map((d, i) => ({
        dayNumber: d.day || i + 1,
        title: d.title || '',
        stops: (d.activities || []).map(a => ({
          ref: a.ref,
          name: a.name || a.activity || '',
          lat: a.lat,
          lng: a.lng,
          durationMin: a.durationMin,
          // `kind` lets the backend skip static meal breaks (Lunch/Dinner) when
          // re-packing; `kbId` lets it rank surviving places by real demand so
          // the highest-priority stops are the ones kept.
          kind: a.kind,
          kbId: a.kbId,
        })),
      })),
    };
    setSwapLoading(true);
    setSwapError(null);
    try {
      const result = await ai.daytripSwap(payload);
      setDayTripPlans(prev => [...prev, result]);
      setSwapTarget(null);
      setActiveDay(0);
      // Build from current live itinerary so we preserve local fields across
      // multiple sequential swaps
      const baseForMerge = itin || generated;
      const locs = await geocodeItinerary(swapResultToItin(result, baseForMerge));
      setMapLocations(locs);
    } catch (err) {
      console.error('Day trip swap error:', err.message);
      setSwapError(err.message || 'Failed to build the day trip — please try again.');
    } finally {
      setSwapLoading(false);
    }
  };

  // Restore the pre-swap plans. Clear all day trips.
  const undoDayTrip = async () => {
    setDayTripPlans([]);
    const restored = baseline && showBaseline ? baseline : generated;
    if (!restored || !restored.days) return;
    const locs = await geocodeItinerary(restored);
    setMapLocations(locs);
  };

  const toggleDayTrips = () => setPlan(p => ({ ...p, includeDayTrips: !p.includeDayTrips }));

  // The swap sheet is a modal: Escape closes it — except mid-flight, when a
  // dismissal would orphan the in-progress repack (same rule as the backdrop).
  useEffect(() => {
    if (!swapTarget) return;
    const onKey = e => { if (e.key === 'Escape' && !swapLoading) setSwapTarget(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [swapTarget, swapLoading]);

  // The itinerary currently on screen. Stored swap results are applied
  // sequentially on top of each other (each on the current live itinerary).
  const comparing = Boolean(baseline);
  let swapItin = null;
  if (dayTripPlans.length > 0) {
    let currentBase = generated;
    let current = generated;
    for (const planRes of dayTripPlans) {
      current = swapResultToItin(planRes, currentBase);
      currentBase = current;
    }
    swapItin = currentBase;
  }
  const itin = swapItin || (comparing && showBaseline ? baseline : generated);
  const usedTripKeys = new Set(
    (dayTripPlans || []).map(p => p?.dayTrip?.cityName || p?.dayTrip?.title || p?.title).filter(Boolean)
  );
  const plannableTrips = dayTrips.filter(dt => dt.plannable && !usedTripKeys.has(dt.title) && !usedTripKeys.has(dt.cityName));

  // Switch model and re-plan. The pool and preferences are unchanged, so this is
  // the same request with one field different — cheap, and it lets the user feel
  // the difference between the models rather than read about it.
  const chooseModel = async (id) => {
    if (id === model || !generated) return;
    setModel(id);
    await generate(id);
  };

  const handleRefine = async () => {
    if (!refinePrompt.trim() || !itin || !plan.dest) return;
    if (!user) { requireAuth(); return; }
    setRefining(true);
    setRefineError(null);
    try {
      let baseForRefine = itin;
      // If there are dropped stops from daytrip swaps, merge them into the itinerary
      // so the refine can consider them when the user asks to include them
      const allDropped = (dayTripPlans || []).flatMap((p, pidx) => (Array.isArray(p.dropped) ? p.dropped.map((dr, idx) => ({ dr, key: `${pidx}-${idx}` })) : []));
      if (allDropped.length > 0) {
        baseForRefine = JSON.parse(JSON.stringify(itin));
        if (!baseForRefine.days || baseForRefine.days.length === 0) {
          baseForRefine.days = [{ day: 1, title: 'Day 1', activities: [] }];
        }
        const lastDay = baseForRefine.days[baseForRefine.days.length - 1];
        if (!Array.isArray(lastDay.activities)) lastDay.activities = [];
        allDropped.forEach(({ dr, key }) => {
          lastDay.activities.push({
            activity: dr.name,
            name: dr.name,
            time: '',
            description: dr.reason || 'Could not be re-packed during day trip swap',
            ref: `dropped-${key}`,
          });
        });
      }
      const result = await ai.refine(slimForRefine(baseForRefine), refinePrompt, {
        transport: plan.transport,
        style: plan.budgetLabel,
        pace: plan.pace,
        travelers: plan.who,
        travelersLabel: plan.whoLabel,
        budgetMult: plan.budgetMult,
      });
      if (!result?.itinerary?.days?.length) {
        throw new Error('The AI returned an empty itinerary. Try rephrasing your request.');
      }
      const refined = carryOverLocalFields(result.itinerary, baseForRefine);
      if (estPrice()) refined.totalBudgetEstimate = money(estPrice());
      if (!baseline) setBaseline(generated);
      if (dayTripPlans.length > 0) {
        setDayTripPlans([]);
      }
      setGenerated(refined);
      setRefineLog(log => [...log, refinePrompt.trim()]);
      setShowBaseline(false);
      setMeta({ ...meta, grounded: false, mock: false, cached: false, });
      const newLocations = await geocodeItinerary(refined);
      setMapLocations(newLocations);
      setShowRefine(false);
      setRefinePrompt('');
      setActiveDay(0);
    } catch (err) {
      console.error('Refine error:', err.message);
      setRefineError(err.message || 'Failed to refine — please try again.');
    } finally {
      setRefining(false);
    }
  };

  const renderCalPanel = (role, stepIdx) => {
    const offset = role === 'start' ? calStartOffset : calEndOffset;
    const base = new Date(); base.setHours(0, 0, 0, 0); base.setDate(1); base.setMonth(base.getMonth() + offset);
    const y = base.getFullYear(), m = base.getMonth();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const firstDay = new Date(y, m, 1).getDay(), daysIn = new Date(y, m + 1, 0).getDate();
    let minE = 0;
    if (plan.startDate) { const nextDay = new Date(plan.startDate); nextDay.setDate(nextDay.getDate() + 1); minE = monthOffset(nextDay); }
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(<span key={'e' + i} className="cal-cell empty" />);
    for (let d = 1; d <= daysIn; d++) {
      const date = new Date(y, m, d); date.setHours(0, 0, 0, 0);
      let disabled = date < today;
      if (role === 'return' && plan.startDate) disabled = disabled || date <= plan.startDate;
      const isStart = plan.startDate && date.getTime() === plan.startDate.getTime();
      const isEnd = plan.endDate && date.getTime() === plan.endDate.getTime();
      const inrange = plan.startDate && plan.endDate && date > plan.startDate && date < plan.endDate;
      const sel = role === 'start' ? isStart : isEnd;
      cells.push(
        <button key={d} disabled={disabled}
          className={`cal-cell ${disabled ? 'past' : ''} ${sel ? 'sel' : ''} ${inrange ? 'inrange' : ''}`}
          onClick={() => (role === 'start' ? chooseStart(date) : chooseReturn(date, stepIdx))}>{d}</button>
      );
    }
    const prevDisabled = role === 'start' ? offset <= 0 : offset <= minE;
    const onPrev = () => (role === 'start' ? setCalStartOffset(o => Math.max(0, o - 1)) : setCalEndOffset(o => Math.max(minE, o - 1)));
    const onNext = () => (role === 'start' ? setCalStartOffset(o => o + 1) : setCalEndOffset(o => o + 1));
    const labelDate = role === 'start' ? (plan.startDate ? fmtDate(plan.startDate) : '') : (plan.endDate ? fmtDate(plan.endDate) : '');
    return (
      <div className="cal" key={role}>
        <div className="cal-label">{role === 'start' ? '🛫 Departure' : '🛬 Return'}{labelDate ? <> · <span style={{ color: B.forest }}>{labelDate}</span></> : null}</div>
        <div className="cal-head">
          <button className="cal-nav" onClick={onPrev} disabled={prevDisabled}>‹</button>
          <b>{base.toLocaleString('en-US', { month: 'long', year: 'numeric' })}</b>
          <button className="cal-nav" onClick={onNext}>›</button>
        </div>
        <div className="cal-grid cal-dow"><span>S</span><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span></div>
        <div className="cal-grid">{cells}</div>
      </div>
    );
  };

  const renderOpts = (s, i) => {
    if (s.type === 'dest') {
      return (
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1.5px solid #e7e2d9', borderRadius: 50, padding: '10px 18px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={B.muted} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
              <input value={destSearch} onChange={e => setDestSearch(e.target.value)}
                placeholder="Search any destination..."
                style={{ border: 'none', fontSize: 14, outline: 'none', width: '100%', fontFamily: "'Montserrat',sans-serif", background: 'transparent' }} />
              {destSearching && <span style={{ color: B.muted, fontSize: 12 }}>…</span>}
            </div>
          </div>
          {destSearch.length >= 1 && destResults.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 16, marginBottom: 20 }}>
              {destResults.map(d => (
                <DestinationCard
                  key={d.id}
                  destination={d}
                  onClick={() => selectDestResult(i, d)}
                  showPartners={false}
                />
              ))}
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 12, color: B.muted, fontWeight: 600, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>POPULAR DESTINATIONS</p>
              <div className="opt-grid">
                {destList
                  .filter(d => !d.name.toLowerCase().includes('safari'))
                  .slice(0, destCount)
                  .map(d => (
                    <button key={d.id} className={`opt opt-dest ${plan.dest && plan.dest.id === d.id ? 'on' : ''}`} onClick={() => chooseDest(i, d)}>
                      <FallbackImg sources={[d.imgUrl || d.img, ...(d.imgFallbacks || [])]} alt={d.name} />
                      <div className="t"><h4>{d.name.split(',')[0]}</h4><p>{d.tag}</p></div>
                    </button>
                  ))}
              </div>
              {destList.length > destCount && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button variant="secondary" size="md" onClick={() => setDestCount(destList.length)}>
                    Show more ({destList.length - destCount}) →
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    if (s.type === 'date') {
      return (
        <div>
          <div className="cal-dual">
            {renderCalPanel('start', i)}
            {plan.startDate ? renderCalPanel('return', i) : (
              <div className="cal cal-ghost"><div className="cal-label">🛬 Return</div><div className="cal-ghost-msg">Pick your departure date<br />and the return calendar appears here</div></div>
            )}
          </div>
          <button className="flex-chip" onClick={() => chooseFlexible(i)}>🤷 I'm flexible with dates</button>
          {plan.flexible && (
            <div className="flex-days" role="group" aria-label="Trip length">
              <span>How many days?</span>
              <button type="button" aria-label="Fewer days" disabled={(plan.duration || 7) <= 1}
                onClick={() => setFlexibleDays((plan.duration || 7) - 1)}>−</button>
              <select aria-label="Number of days" value={plan.duration || 7} onChange={(e) => setFlexibleDays(Number(e.target.value))}>
                {FLEX_DAY_OPTIONS.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'day' : 'days'}</option>)}
              </select>
              <button type="button" aria-label="More days" disabled={(plan.duration || 7) >= 14}
                onClick={() => setFlexibleDays((plan.duration || 7) + 1)}>+</button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, background: '#f3fbf6', border: '1px solid #cfe7d9', borderRadius: 12, padding: '12px 14px' }}>
            <button
              type="button"
              role="switch"
              aria-label="Day trips"
              aria-checked={plan.includeDayTrips}
              onClick={toggleDayTrips}
              style={{
                width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: plan.includeDayTrips ? B.forest : '#d9d4cb', position: 'relative', padding: 0, flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: plan.includeDayTrips ? 23 : 3, width: 20, height: 20,
                borderRadius: '50%', background: 'white', transition: 'left 0.18s ease',
              }} />
            </button>
            <div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700, color: B.night }}>Day trips</div>
              <div style={{ color: B.muted, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>
                Swap one day of the plan for a fast trip to a nearby city
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="opt-grid">
        {s.opts.map((o, oi) => {
          const on = (s.key === 'pace' && plan.pace === o.v) || (s.key === 'budget' && plan.budgetMult === o.v) || (s.key === 'who' && plan.who === o.v) || (s.key === 'transport' && plan.transport === o.v) || (s.key === 'interests' && (plan.interests || []).includes(o.v));
          return (
            <button key={oi} className={`opt ${on ? 'on' : ''}`} aria-pressed={s.multi ? on : undefined} onClick={() => chooseOpt(i, o)}>
              <span className="em">{o.em}</span><h4>{o.h}</h4>{o.p && <p>{o.p}</p>}
            </button>
          );
        })}
      </div>
    );
  };

  if (view === 'loading') {
    // Label, not the raw `plan.pace` id — the h2 above already says "Crafting".
    const paceLabel = pickText(PSTEPS.find(x => x.key === 'pace'));
    return (
      <div className="aip"><style>{CSS}</style>
        <TripCraftingLoader city={plan.dest.name.split(',')[0]} days={plan.duration || 7} pace={paceLabel} budget={plan.budgetLabel} />
      </div>
    );
  }

  if (view === 'itinerary' && generated) {
    const d = plan.dest;
    // `itin`/`comparing` are resolved at component scope (see above) so the swap
    // sheet can read whichever plan is actually on screen.
    const rangeLabel = plan.startDate ? `${fmtDate(plan.startDate)} – ${fmtDate(plan.endDate)}` : (plan.flexible ? 'Flexible dates' : '');
    const badge = meta.grounded
        ? { text: '✓ Verified real places', bg: B.forest }
        : { text: '✨ AI-generated', bg: B.forest };
    const estTotal = itin.totalBudgetEstimate || (estPrice() ? money(estPrice()) : '—');
    // Clamp: the refined plan can have fewer days than the original, so toggling
    // to a shorter plan while a later day is selected would index past the end.
    const dayIdx = Math.min(activeDay, Math.max(0, itin.days.length - 1));
    const current = itin.days[dayIdx];
    const travel = travelSummary(itin);
    const transportNote = TRANSPORT_NOTES[plan.transport] || TRANSPORT_NOTES.transit;
    return (
      <div className="aip"><style>{CSS}</style>
        <div className="detail-hero">
          <FullPhoto sources={[d.imgLg || d.img]} alt={d.name} />
          <div className="ov" />
          <button className="back" onClick={() => setView('plan')}>← Edit plan</button>
          <div className="info">
            <span className="chip-tag" style={{ marginBottom: 8, background: badge.bg, color: '#fff' }}>{badge.text}</span>
            <h1 className="serif">{itin.title || `${plan.duration || 7} days in ${d.name.split(',')[0]}`}</h1>
            {rangeLabel && <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, opacity: .95 }}>📅 {rangeLabel}</div>}
          </div>
        </div>
        <div className="wrap" style={{ paddingBottom: 60 }}>
          {itin.summary && <p style={{ color: B.muted, margin: '20px 0 0', maxWidth: 760, lineHeight: 1.6 }}>{itin.summary}</p>}

          {/* Refinement comparison. The original is kept intact and shown beside
              the refined plan; further refinements stack on the refined one, and
              nothing is overwritten until the user picks a winner. A live day-trip
              swap hides it — the swap has its own Undo, and refining on top of a
              swap would silently target the wrong plan. */}
          {comparing && dayTripPlans.length === 0 && (
            <div style={{
              marginTop: 24, padding: '16px 18px', borderRadius: 14,
              background: 'rgba(245,166,35,0.10)', border: `1px solid ${B.amber}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 15 }}>Comparing your refinements</b>
                <span style={{ color: B.muted, fontSize: 13 }}>
                  {refineLog.length === 1 ? '1 change' : `${refineLog.length} changes`} applied — keep whichever you prefer.
                </span>
              </div>

              {refineLog.length > 0 && (
                <ol style={{ margin: '10px 0 0 18px', padding: 0, color: B.muted, fontSize: 13, lineHeight: 1.6 }}>
                  {refineLog.map((r, i) => <li key={i}>“{r}”</li>)}
                </ol>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <div style={{ display: 'inline-flex', borderRadius: 50, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)' }}>
                  {[false, true].map(showOrig => (
                    <button
                      key={String(showOrig)}
                      onClick={() => viewPlan(showOrig)}
                      aria-pressed={showBaseline === showOrig}
                      style={{
                        padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                        fontFamily: "'Montserrat',sans-serif",
                        background: showBaseline === showOrig ? B.night : 'transparent',
                        color: showBaseline === showOrig ? '#fff' : B.muted,
                      }}>
                      {showOrig ? 'Original' : 'Refined'}
                    </button>
                  ))}
                </div>
                <button onClick={keepRefined}
                  style={{
                    padding: '9px 20px', borderRadius: 50, border: 'none', background: B.forest,
                    color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Montserrat',sans-serif",
                  }}>
                  Keep refined
                </button>
                <button onClick={keepOriginal}
                  style={{
                    padding: '9px 20px', borderRadius: 50, border: '1px solid rgba(0,0,0,0.15)',
                    background: 'transparent', color: B.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Montserrat',sans-serif",
                  }}>
                  Discard changes
                </button>
              </div>

              <p style={{ color: B.muted, fontSize: 12, marginTop: 10 }}>
                You are viewing the <b>{showBaseline ? 'original' : 'refined'}</b> plan.
                Refining again builds on the refined one.
              </p>
            </div>
          )}

          {/* How the days were built. Descriptions state what each one measurably does,
              not what it aspires to — see PLANNER_MODELS for the figures behind them.
              'anchor' is the default in both this screen and routes/ai.js. */}
          <div style={{ marginTop: 28 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <h3 className="serif" style={{ fontSize: 20 }}>How these days are built</h3>
              <span style={{ color: B.muted, fontSize: 13 }}>
                Same verified places, arranged three different ways — switch to re-plan.
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12, marginTop: 14 }}>
              {PLANNER_MODELS.map(m => {
                const on = m.id === model;
                return (
                  <button
                    key={m.id}
                    onClick={() => chooseModel(m.id)}
                    aria-pressed={on}
                    disabled={on}
                    style={{
                      textAlign: 'left', padding: '14px 16px', borderRadius: 14, cursor: on ? 'default' : 'pointer',
                      background: on ? 'rgba(78,203,141,0.12)' : '#16222e',
                      border: `1px solid ${on ? B.mint : 'rgba(255,255,255,0.08)'}`,
                      color: on ? B.night : '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{m.em}</span>
                      <b style={{ fontSize: 15 }}>{m.label}</b>
                      {on && <span style={{ marginLeft: 'auto', fontSize: 11, color: B.mint, fontWeight: 700 }}>IN USE</span>}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: B.mint, marginTop: 6 }}>{m.short}</div>
                    <p style={{ color: on ? B.muted : 'rgba(255,255,255,0.72)', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{m.d}</p>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="stat-row">
            <div className="stat"><b>{itin.days.length}</b><span>DAYS</span></div>
            <div className="stat"><b>{plan.whoLabel}</b><span>TRAVELERS</span></div>
            <div className="stat"><b>{plan.budgetLabel}</b><span>STYLE</span></div>
            <div className="stat"><b>{PSTEPS.find(x => x.key === 'pace')?.opts?.find(o => o.v === plan.pace)?.sum || '—'}</b><span>PACE</span></div>
            <div className="stat"><b>{estTotal}</b><span>EST. TOTAL</span></div>
          </div>
          <div className="itin-actions" style={{ display: 'flex', gap: 12, margin: '6px 0 24px', flexWrap: 'wrap' }}>
            <button className="it-btn it-forest" onClick={saveItinerary} disabled={saving}>{saving ? '⏳ Saving…' : '💾 Save to My Trips'}</button>
            {dayTripPlans.length === 0 && <button className="it-btn it-out" onClick={() => setShowRefine(true)}>✏️ Refine with AI</button>}
            <button className="it-btn it-out" onClick={() => shareItinerary(itin)} disabled={saving}>↗ Share</button>
            <FeedbackButton className="it-btn it-out" context="itinerary" destination={plan.dest && plan.dest.name} />
          </div>
          {shareError && <div role="alert" style={{ color: '#b42318', fontSize: 13, margin: '-14px 0 18px' }}>{shareError}</div>}
          {shareTrip && <ShareModal trip={shareTrip} shareText={shareTrip.__shareText} onClose={() => setShareTrip(null)} />}
          {sharePrompt && (
            <Modal isOpen={true} onClose={() => setSharePrompt(null)} title="Share your trip" size="md">
              <div style={{ padding: 20 }}>
                <p style={{ fontSize: 14, color: B.night, marginBottom: 12, lineHeight: 1.6 }}>
                  This trip will be saved to your account and made public to share.
                </p>
                <label style={{ display: 'block', fontSize: 12, color: B.muted, marginBottom: 6, fontWeight: 600 }}>Share message</label>
                <textarea
                  value={sharePrompt.message}
                  onChange={(e) => setSharePrompt({ ...sharePrompt, message: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button
                    onClick={() => setSharePrompt(null)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      color: B.night,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 14
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmShareItinerary}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 10,
                      border: 'none',
                      background: B.forest,
                      color: 'white',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: 14,
                      opacity: saving ? 0.7 : 1
                    }}
                  >
                    {saving ? 'Saving…' : 'Save & Share'}
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {showRefine && (
            <div style={{
              background: 'white', border: '1px solid #e5e0d8', borderRadius: 12, padding: 20,
              marginBottom: 24, maxWidth: 480,
            }}>
              <h4 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>✏️ Refine your itinerary</h4>
              <p style={{ color: B.muted, fontSize: 12, marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>
                Tell us what you'd like to change — we'll regenerate with your preferences.
              </p>
              <textarea
                value={refinePrompt}
                onChange={e => setRefinePrompt(e.target.value)}
                placeholder="e.g., Make day 2 more relaxing, add more food experiences..."
                style={{
                  width: '100%', padding: 12, borderRadius: 12, border: '2px solid #e5e0d8',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 14, minHeight: 90,
                  resize: 'none', marginBottom: 12, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                {['Make it more relaxed', 'Add more food', 'Reduce budget', 'Add free time'].map(s => (
                  <button key={s} onClick={() => setRefinePrompt(s)}
                    style={{
                      padding: '5px 12px', borderRadius: 50, border: '1px solid #e5e0d8',
                      background: refinePrompt === s ? B.forest : 'white', color: refinePrompt === s ? 'white' : B.muted,
                      fontSize: 12, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                      transition: 'all 0.15s',
                    }}>
                    {s}
                  </button>
                ))}
              </div>
              {refineError && (
                <p style={{ color: '#e74c3c', fontSize: 12, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>
                  ⚠ {refineError}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleRefine}
                  disabled={refining || !refinePrompt.trim()}
                  style={{
                    padding: '10px 24px', borderRadius: 50, border: 'none',
                    background: !refinePrompt.trim() ? '#ccc' : B.forest, color: 'white',
                    fontSize: 13, fontWeight: 700, cursor: !refinePrompt.trim() ? 'default' : 'pointer',
                    fontFamily: "'Montserrat',sans-serif",
                  }}>
                  {refining ? '⏳ Refining…' : 'Apply Changes'}
                </button>
                <button onClick={() => { setShowRefine(false); setRefinePrompt(''); setRefineError(null); }}
                  style={{
                    padding: '10px 24px', borderRadius: 50, border: '1px solid #e5e0d8',
                    background: 'white', color: B.muted, fontSize: 13, cursor: 'pointer',
                    fontFamily: "'Montserrat',sans-serif",
                  }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Split-pane: Timeline (left 40%) + Map (right 60%) */}
          <ErrorBoundary name="split-pane">
          <div className="split-pane" style={{ display: 'flex', alignItems: 'flex-start', border: '1px solid #e5e0d8', borderRadius: 12 }}>
            {/* Left: Day selector + activity cards */}
            <ErrorBoundary name="left-pane">
            <div className="split-left" style={{ width: '40%', alignSelf: 'stretch', padding: '24px 20px', background: B.sandPattern, borderRight: '1px solid #e5e0d8', borderRadius: '12px 0 0 12px' }}>
              <div className="day-tabs-wrap" style={{ marginBottom: 24 }}>
                <ErrorBoundary name="Tab">
                <div role="tablist" aria-label="Days" className="day-grid"
                  style={{ gridTemplateColumns: `repeat(${Math.min(itin.days.length, 7)}, minmax(0, 1fr))` }}>
                  {itin.days.map((day, i) => {
                    const on = i === dayIdx;
                    return (
                      <button key={day.day} type="button" role="tab" aria-selected={on} aria-label={`Day ${day.day}`}
                        tabIndex={on ? 0 : -1} className={on ? 'on' : undefined}
                        onClick={() => setActiveDay(i)}
                        onKeyDown={(e) => {
                          const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
                          if (next == null || next < 0 || next >= itin.days.length) return;
                          e.preventDefault();
                          setActiveDay(next);
                          e.currentTarget.parentElement.children[next]?.focus();
                        }}>
                        {itin.days.length > 5 ? `D${day.day}` : `Day ${day.day}`}
                      </button>
                    );
                  })}
                </div>
                </ErrorBoundary>
              </div>

              <ErrorBoundary name="activity-cards">
              {current && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <span style={{ fontSize: 28 }}>{current.icon || '📍'}</span>
                    <div style={{ minWidth: 0 }}>
                      {current.dayTrip ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, margin: 0 }}>
                              {current.dayTrip.title}
                            </h3>
                            <span style={{
                              fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 20,
                              background: 'rgba(245,166,35,.2)', color: '#a86f10',
                              fontFamily: "'Montserrat',sans-serif", letterSpacing: .4, whiteSpace: 'nowrap',
                            }}>
                              DAY TRIP
                            </span>
                          </div>
                          <p style={{ color: B.muted, fontSize: 12, fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>
                            {dayTripTransportEmoji(current.dayTrip.transport)} {d.name.split(',')[0]} → {current.dayTrip.cityName} → back by EOD ·{' '}
                            <b style={{ color: B.forest }}>≈{humanizeMinutes(current.dayTrip.legMin)} each way</b>
                          </p>
                          {current.dayTrip.warning && (
                            <div style={{
                              display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 8,
                              background: 'rgba(245,166,35,.08)', border: '1px dashed rgba(168,111,16,.4)',
                              borderRadius: 8, padding: '7px 10px',
                              color: '#a86f10', fontSize: 12, fontWeight: 600,
                              fontFamily: "'Montserrat',sans-serif",
                            }}>
                              <span>⚠️</span>
                              <span>{current.dayTrip.warning.message}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
                            {current.title}
                          </h3>
                          <p style={{ color: B.muted, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>
                            {current.theme}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {current.activities?.map((act, i) => {
                      const hovered = hoveredActivity &&
                        hoveredActivity.activityIndex === i &&
                        hoveredActivity.dayNumber === current.day;
                      const setHover = () => setHoveredActivity({ ...act, activityIndex: i, dayNumber: current.day });
                      return (
                        <React.Fragment key={i}>
                          {i > 0 && act.travel && (
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                              padding: '6px 0', fontSize: 12, color: B.muted,
                              fontFamily: "'Montserrat',sans-serif",
                            }}>
                              <div style={{ width: 1, height: 16, background: '#ddd' }} />
                              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span>{modeMeta(act.travel.mode).em}</span>
                                <span>{act.travel.minutes} min</span>
                                <span style={{ color: '#ccc' }}>·</span>
                                <span>{act.travel.km} km</span>
                                {directionsUrl(current.activities[i - 1], act, act.travel.mode || plan.transport) && (
                                  <>
                                    <span style={{ color: '#ccc' }}>·</span>
                                    <a href={directionsUrl(current.activities[i - 1], act, act.travel.mode || plan.transport)} target="_blank" rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label={`Directions to ${act.activity || 'next stop'}`}
                                      style={{ color: B.forest, fontWeight: 600, textDecoration: 'none' }}>Directions ↗</a>
                                  </>
                                )}
                              </span>
                              <div style={{ width: 1, height: 16, background: '#ddd' }} />
                            </div>
                          )}
                          <div
                            onClick={setHover}
                            style={{
                              background: hovered ? 'rgba(26,107,74,0.05)' : 'white',
                              border: hovered ? `2px solid ${B.forest}` : '1px solid #e5e0d8',
                              borderRadius: 12,
                              padding: 16,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              overflow: 'hidden',
                            }}
                            onMouseEnter={setHover}
                            onMouseLeave={() => setHoveredActivity(null)}
                          >
                          {act.photoUrl && (
                            <div style={{ position: 'relative', width: 'calc(100% + 32px)', height: 120, overflow: 'hidden', borderRadius: '12px 12px 0 0', margin: '-16px -16px 12px -16px', background: B.night }}>
                              <FullPhoto sources={[act.photoUrl]} alt={act.activity} />
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ color: B.forest, fontSize: 12, fontWeight: 600, fontFamily: "'Montserrat',sans-serif" }}>
                              {act.time}
                              {act.kind !== 'meal' && Number.isFinite(act.durationMin) && act.durationMin > 0 && (
                                <span style={{ color: B.muted, fontWeight: 500 }}>
                                  {' · ⏱ '}{act.durationMin >= 60 ? `${Math.floor(act.durationMin / 60)}h${act.durationMin % 60 ? ` ${act.durationMin % 60}m` : ''}` : `${act.durationMin} min`} visit
                                </span>
                              )}
                            </span>
                            {act.cost && (
                              <span style={{ color: B.amber, fontSize: 12, fontWeight: 600, fontFamily: "'Montserrat',sans-serif" }}>
                                {act.cost}
                              </span>
                            )}
                          </div>
                          <h4 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                            {act.activity}
                          </h4>
                          {act.movedFrom && (
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6,
                              padding: '2px 8px', borderRadius: 50, background: 'rgba(78,203,141,.12)',
                              color: B.forest, fontSize: 11, fontWeight: 700,
                              fontFamily: "'Montserrat',sans-serif",
                            }}>
                              ↪ moved from {act.movedFrom}
                            </div>
                          )}
                          <ApproximateLocationNote approximate={act.locationApproximate} />
                          {act.description && (
                            <p style={{ color: B.muted, fontSize: 13, lineHeight: 1.5, fontFamily: "'Montserrat',sans-serif" }}>
                              {act.description}
                            </p>
                          )}
                          {act.tip && (
                            <p style={{ color: B.mint, fontSize: 12, marginTop: 8, fontStyle: 'italic', fontFamily: "'Montserrat',sans-serif" }}>
                              💡 {act.tip}
                            </p>
                          )}
                          {act.kind !== 'meal' && Number.isInteger(act.kbId) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenPlace({ id: act.kbId, name: act.activity, lat: act.lat, lng: act.lng, imageUrl: act.photoUrl || null });
                              }}
                              aria-label={`More popular info about ${act.activity}`}
                              style={{
                                marginTop: 10, padding: '6px 12px', borderRadius: 50,
                                border: `1px solid ${B.forest}`, background: 'white', color: B.forest,
                                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                fontFamily: "'Montserrat',sans-serif",
                              }}
                            >
                              More popular info →
                            </button>
                          )}
                        </div>
                      </React.Fragment>
                    );
                    })}
                  </div>
                </div>
              )}
              </ErrorBoundary>
            </div>
            </ErrorBoundary>

            {/* Right: Map */}
            <ErrorBoundary name="map">
            <div className="split-right" style={{ width: '60%', position: 'sticky', top: 84, height: 'calc(100vh - 100px)', borderRadius: '0 12px 12px 0', overflow: 'hidden' }}>
              {mapLocations.length > 0 ? (
                isPhone ? (
                  <MapView locations={mapLocations.filter(l => l.dayNumber === dayIdx + 1)} hoveredActivity={hoveredActivity} onActivityHover={setHoveredActivity} height={240} />
                ) : (
                  <div style={{ position: 'absolute', inset: 0 }}>
                    <MapView locations={mapLocations.filter(l => l.dayNumber === dayIdx + 1)} hoveredActivity={hoveredActivity} onActivityHover={setHoveredActivity} height="100%" />
                  </div>
                )
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
                  📍 No map locations available
                </div>
              )}
            </div>
            </ErrorBoundary>
          </div>
          </ErrorBoundary>

          {/* Dropped stops from any daytrip swap */}
          {dayTripPlans.some(p => Array.isArray(p.dropped) && p.dropped.length > 0) && (
            <div style={{ background: 'rgba(245,166,35,.08)', border: '1px dashed rgba(168,111,16,.4)', borderRadius: 14, padding: '14px 16px', marginTop: 18 }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800, color: '#a86f10', letterSpacing: .5, textTransform: 'uppercase', marginBottom: 8 }}>
                Couldn't re-pack
              </div>
              {(dayTripPlans || []).flatMap((p, pidx) => (Array.isArray(p.dropped) ? p.dropped.map((dr, i) => (
                <div key={`${pidx}-${dr.name}-${i}`} style={{ color: B.muted, fontSize: 13, fontFamily: "'Montserrat',sans-serif", marginBottom: 4 }}>
                  <b style={{ color: B.night }}>{dr.name}</b>{dr.reason ? ` — ${dr.reason}` : ''}
                </div>
              )) : []))}
            </div>
          )}

          {/* Undo: rendered whenever any swap result is live */}
          {dayTripPlans.length > 0 && (
            <button
              type="button"
              onClick={undoDayTrip}
              style={{
                marginTop: 18, background: 'transparent', border: '1px solid rgba(33,86,60,.5)', color: B.forest,
                borderRadius: 50, padding: '7px 14px', fontSize: 12, fontWeight: 700,
                fontFamily: "'Montserrat',sans-serif", cursor: 'pointer',
              }}
            >
              ↩ Undo {dayTripPlans.length === 1 ? 'day trip' : 'day trips'}
            </button>
          )}

          {/* Stats row */}
          <div className="itin-stats-bottom" style={{ display: 'flex', gap: 12, padding: '28px 0', flexWrap: 'wrap' }}>
            {[
              { icon: '💰', label: 'Budget Est.', value: estTotal },
              { icon: '📅', label: 'Duration', value: `${itin.days.length} Days` },
              { icon: '🌤️', label: 'Best Time', value: itin.bestTimeToVisit || cityBestMonths || '—' },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{ background: 'white', borderRadius: 12, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div>
                  <div style={{ color: B.muted, fontSize: 10, fontFamily: "'Montserrat',sans-serif", letterSpacing: 1.5, textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ color: B.night, fontSize: 14, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Day-trips rail: plannable one-city trips reachable from the home city.
              Appears only when To-Do in the plan includes day trips. A home city
              whose catalog is all info-only trips has no plannable subset, so no
              rail — only a misleading empty header would render here. */}
          {plan.includeDayTrips && plannableTrips.length > 0 && (
            <div style={{ background: '#f3fbf6', border: '1px solid #cfe7d9', borderRadius: 16, padding: '18px 20px', marginTop: 4 }}>
              <h3 className="serif" style={{ fontSize: 20, marginBottom: 4 }}>Day trips from {d.name.split(',')[0]}</h3>
              <p style={{ color: B.muted, fontSize: 13, fontFamily: "'Montserrat',sans-serif" }}>
                A few hours to a full day in a nearby city — we re-pack a day of your plan around it.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
                {plannableTrips.map(dt => (
                  <div key={dt.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'white', border: '1px solid #e7e2d9', borderRadius: 12, padding: '12px 14px' }}>
                    <span style={{ fontSize: 20 }}>🚗</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, fontFamily: "'Montserrat',sans-serif", color: B.night }}>
                        {dt.title}
                      </div>
                      <div style={{ color: B.muted, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>
                        {dt.cityName}
                        {dt.travelMinutes != null && <> · <span>{humanizeMinutes(dt.travelMinutes)} each way</span></>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => openSwap(dt)}
                      style={{
                        background: B.forest, color: 'white', border: 'none', padding: '8px 16px',
                        borderRadius: 24, fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif",
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      Add to plan
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Getting around: aggregated per-leg travel + local transport guidance */}
          <div style={{ background: '#fff', border: '1px solid #e7e2d9', borderRadius: 16, padding: '18px 20px' }}>
            <h3 className="serif" style={{ fontSize: 20, marginBottom: 4 }}>Getting around</h3>
            <p style={{ color: B.muted, fontSize: 13, fontFamily: "'Montserrat',sans-serif" }}>
              Planned for {(PSTEPS.find(x => x.key === 'transport')?.opts || []).find(o => o.v === plan.transport)?.h || 'Public Transit'}
              {travel ? ` · about ${travel.perDayMinutes} min of travel per day` : ''}
            </p>
            {travel && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                {travel.modes.map(m => (
                  <div key={m.mode} style={{ border: '1px solid #e7e2d9', borderRadius: 12, padding: '10px 14px', minWidth: 150 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
                      {modeMeta(m.mode).em} {modeMeta(m.mode).label}
                    </div>
                    <div style={{ color: B.muted, fontSize: 12, marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>
                      {m.legs} {m.legs === 1 ? 'hop' : 'hops'} · {m.minutes} min · {m.km} km
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p style={{ color: B.muted, fontSize: 13, lineHeight: 1.6, marginTop: 12, fontFamily: "'Montserrat',sans-serif" }}>
              🎫 {transportNote}
            </p>
          </div>

          {itin.packingTips && itin.packingTips.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 className="serif" style={{ fontSize: 20, marginBottom: 8 }}>Packing tips</h3>
              <ul style={{ color: B.muted, paddingLeft: 18, lineHeight: 1.7 }}>{itin.packingTips.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </div>
          )}

          {/* Day-trip swap sheet: choose which day of the plan gives way. The
              default is the day with the fewest stops, so the swap disturbs the
              least of the plan. The repack itself is deterministic on the backend. */}
          {swapTarget && (
            <div
              onClick={() => { if (!swapLoading) setSwapTarget(null); }}
              style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(18,23,20,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            >
              <div
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                style={{ background: 'white', borderRadius: 18, maxWidth: 460, width: '100%', padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <span style={{ fontSize: 26 }}>🚗</span>
                  <h3 className="serif" style={{ fontSize: 20, margin: 0 }}>Add {swapTarget.title} to the plan?</h3>
                </div>
                <p style={{ color: B.muted, fontSize: 13, margin: '10px 0 16px', lineHeight: 1.5 }}>
                  One day of the plan becomes {swapTarget.title} — its stops get re-packed into the other days.
                </p>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: B.muted, marginBottom: 8 }}>
                  Which day gives way?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="radiogroup" aria-label="Which day gives way?">
                  {itin.days.map(d => (
                    <div
                      key={d.day}
                      role="radio"
                      aria-checked={swapDayNumber === d.day}
                      tabIndex={0}
                      onClick={() => setSwapDayNumber(d.day)}
                      onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setSwapDayNumber(d.day); } }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
                        border: `2px solid ${swapDayNumber === d.day ? B.forest : '#e7e2d9'}`,
                        background: swapDayNumber === d.day ? 'rgba(78,203,141,.08)' : 'white',
                        fontFamily: "'Montserrat',sans-serif",
                      }}
                    >
                      <span style={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${swapDayNumber === d.day ? B.forest : '#c8c2b7'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white',
                      }}>
                        {swapDayNumber === d.day && <span style={{ width: 8, height: 8, borderRadius: '50%', background: B.forest }} />}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: B.night }}>
                          Day {d.day} · {d.title}
                        </div>
                        <div style={{ color: B.muted, fontSize: 12 }}>
                          {(d.activities || []).length} {(d.activities || []).length === 1 ? 'stop' : 'stops'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {swapError && (
                  <p style={{ color: '#b00020', fontSize: 13, margin: '12px 0 0', fontFamily: "'Montserrat',sans-serif" }}>{swapError}</p>
                )}
                <button
                  type="button"
                  onClick={confirmSwap}
                  disabled={swapLoading || swapDayNumber == null}
                  style={{
                    marginTop: 18, width: '100%', background: B.forest, color: 'white', border: 'none', borderRadius: 28,
                    padding: '13px 18px', fontSize: 14, fontWeight: 800, letterSpacing: .3,
                    fontFamily: "'Montserrat',sans-serif", cursor: 'pointer', opacity: swapLoading ? .7 : 1,
                  }}
                >
                  {swapLoading ? 'Adding…' : 'Add & re-pack days'}
                </button>
              </div>
            </div>
          )}
        </div>
        {openPlace && (
          <PlaceSheet place={openPlace} cityName={d.name.split(',')[0]} categoryLabel={categoryLabel}
            center={Number.isFinite(d.lat) && Number.isFinite(d.lng) ? { lat: d.lat, lng: d.lng } : null}
            onClose={() => setOpenPlace(null)} />
        )}
      </div>
    );
  }

  return (
    <div className="aip"><style>{CSS}</style>
      <div className="wrap">
        <div className="page-head">
          <span className="kicker">AI Trip Planner</span>
          <h1 className="serif">Let's design your perfect trip</h1>
          <p>Answer a few quick questions — we'll build a personalized day-by-day plan.</p>
        </div>
        {genError && (
          <div role="alert" className="gen-error" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 18px', padding: '12px 16px', borderRadius: 12, background: '#fdf1e7', border: `1px solid ${B.amber}`, color: B.night }}>
            <span style={{ flex: '1 1 220px' }}>{genError}</span>
            <button type="button" className="btn-gen" style={{ margin: 0 }} onClick={() => generate()}>Try again</button>
          </div>
        )}
        <div className="plan">
          <div className="plan-steps">
            {PSTEPS.map((s, i) => (
              <div className="pstep" data-state={states[i]} key={s.key} ref={el => { stepRefs.current[i] = el; }}>
                <button type="button" className="collapsed" onClick={() => openEdit(i)}>
                  {s.type === 'dest' && plan.dest ? <img className="thumb" src={plan.dest.imgUrl || plan.dest.img} alt="" /> : null}
                  <div><div className="step-mini">{s.label}</div><div className="pick">{pickText(s)}</div></div>
                  <span className="edit">Edit ✎</span>
                </button>
                <div className="ex-wrap"><div className="ex-inner">
                  <div className="num">STEP {i + 1}</div>
                  <h2 className="serif">{s.title}</h2>
                  {renderOpts(s, i)}
                  {s.key === 'pace' && <button className="btn-cont" disabled={!plan.pace} onClick={() => commitOpt(i)}>Continue →</button>}
                  {s.key === 'budget' && <button className="btn-cont" disabled={!plan.budgetMult} onClick={() => commitOpt(i)}>Continue →</button>}
                  {s.key === 'who' && <button className="btn-cont" disabled={!plan.who} onClick={() => commitOpt(i)}>Continue →</button>}
                  {s.key === 'interests' && <button className="btn-cont" onClick={() => commitOpt(i)}>{(plan.interests || []).length ? 'Continue →' : 'Skip →'}</button>}
                  {s.key === 'transport' && <button className="btn-gen" disabled={!plan.transport} onClick={() => { commitOpt(i); generate(); }}>✨ Generate my itinerary</button>}
                </div></div>
              </div>
            ))}
            {/* Shown only while the transport step is closed — that step renders its
                own generate button, so exactly one is ever visible. */}
            {planReady() && !transportStepOpen && (
              <button className="btn-gen" style={{ marginTop: 16, width: '100%' }} onClick={() => generate()}>✨ Generate my itinerary</button>
            )}
          </div>

          <aside className="summary">
            <div className="top">{plan.dest && <FullPhoto sources={[plan.dest.imgLg || plan.dest.img]} alt="" />}</div>
            <div className="bd">
              <h3>Your trip so far</h3>
              <div>
                {[
                  ['Destination', plan.dest ? plan.dest.name.split(',')[0] : '—'],
                  ['Dates', plan.flexible ? `Flexible · ${plan.duration || 7} ${(plan.duration || 7) === 1 ? 'day' : 'days'}` : (plan.startDate && plan.endDate ? `${fmtDate(plan.startDate)} – ${fmtDate(plan.endDate)}` : '—')],
                  ['Pace', PSTEPS.find(x => x.key === 'pace')?.opts?.find(o => o.v === plan.pace)?.sum || '—'],
                  ['Style', plan.budgetLabel || '—'],
                  ['Travelers', plan.whoLabel || '—'],
                  ['Transport', PSTEPS.find(x => x.key === 'transport')?.opts?.find(o => o.v === plan.transport)?.sum || 'Transit'],
                ].map(([label, value]) => (
                  <div className="sum-row" key={label}><span>{label}</span><span>{value}</span></div>
                ))}
              </div>
              <div className="sum-total"><span>Est. total</span><b>{costLoading ? '…' : estPrice() != null ? money(estPrice()) : '—'}</b></div>
              {estPrice() != null && (
                <div className="cost-note">
                  <div className="cost-note-row">
                    <span>{money(Math.round(cost.range.low))} – {money(Math.round(cost.range.high))}</span>
                    <span>{money(cost.perPersonPerDay)} /person /day</span>
                  </div>
                  {costBreakdownRows().length > 0 && (
                    <div className="cost-breakdown" aria-label="Cost breakdown">
                      {costBreakdownRows().map(([label, value]) => (
                        <div className="cost-note-row" key={label}><span>{label}</span><span>{money(value)}</span></div>
                      ))}
                    </div>
                  )}
                  <div className="cost-note-row cost-note-src cost-scope">
                    For {plan.who || 1} {(plan.who || 1) === 1 ? 'traveler' : 'travelers'} · {plan.duration || 7} {(plan.duration || 7) === 1 ? 'day' : 'days'} · excludes flights, meals{plan.transport === 'car' ? '' : ' and local transport'}
                  </div>
                  {provenanceLabel() && <div className="cost-note-row cost-note-src">{provenanceLabel()}</div>}
                  {cost.medianMealPrice != null && (
                    <div className="cost-note-row cost-note-src">
                      A meal here is about {money(cost.medianMealPrice)} — not included above
                    </div>
                  )}
                </div>
              )}
              {!costLoading && !plan.dest?.costDetail?.hasData && plan.dest && (
                <div className="cost-note cost-note-src">
                  We have no priced data for {plan.dest.name.split(',')[0]} yet
                </div>
              )}
              <button className="sum-gen" disabled={!planReady() || !plan.duration} onClick={() => generate()}>Generate itinerary</button>
            </div>
            <div className="progress-dots">
              {Array.from({ length: GEN }).map((_, i) => <span className={`pdot ${i < answeredCount() ? 'on' : ''}`} key={i} />)}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
